/**
 * ProductFormWizard (S1.3) — the flagship product create/edit wizard.
 *
 * Built on the shared `WizardShell` chrome + `common/form/*` kit +
 * `productFormSchemas.ts` (zodResolver, mode 'onTouched', forward-only
 * per-step trigger() lists). Replaces the codex ProductFormPage.
 *
 * Steps:
 * ① 識別與分類 — SKU with the 800ms-debounced dup-check FSM (ABN-check
 *    pattern: IDLE/VALIDATING/AVAILABLE/TAKEN + "view existing" modal),
 *    Display Name, Subsidiary, TaxonomyCascader (per-level placeholders from
 *    the renamable level labels, inline leaf create via CreateNodeModal),
 *    Include Children, Internal-Product-ID banner (system-assigned).
 * ② 單位與規格 — units cascade (default PCS), dims with live 材積 CBM,
 *    weight, fitment Make/Model multi-select + Year rows via modal,
 *    manufacturer group.
 * ③ 描述與識別碼 — 3 descriptions incl. 報關品名 (live-mirrors Display Name
 *    until edited) with doc-endpoint helper text, external identifiers,
 *    customer PNs, vendor lines (exactly-one-primary), images (max 5,
 *    add hidden at cap — hide-don't-disable). 外部識別 carries the S7.4 OEM
 *    dup-check FSM (mirror of the SKU FSM): same-大類 cross-market twin
 *    lookup via GET /products/check-oem → verbatim board prompt → 選擇式關聯
 *    with auto-carry of the chosen SCN (retroactive PATCH after create —
 *    SCN is NEVER on the create payload). ADVISORY ONLY: errors FAIL OPEN
 *    (prompt suppressed, submit unblocked, error logged).
 * ④ 商務與會計 — costing method, base price, min/max; accounting placeholders
 *    in a collapsed section; CLARIFY → purple `BlockedFieldCallout`
 *    (non-editable 等待決定); WONT fields not rendered.
 * ⑤ 檢視 — `ReadinessMeter` per group (click jumps to the gap) + summary.
 *
 * Create = sequential API calls with `failedOperations[]` partial-failure
 * tracking; success surfaces the assigned Internal Product ID then navigates
 * to detail. Red asterisks = create-required fields ONLY (two-tier readiness).
 */

import { ReactNode, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Col,
  Collapse,
  Dropdown,
  Input,
  InputNumber,
  Modal,
  Popover,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  Upload,
  theme,
} from 'antd';
import {
  CheckCircleFilled,
  CheckCircleOutlined,
  CloseCircleFilled,
  DollarOutlined,
  FileTextOutlined,
  InboxOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  LockOutlined,
  MoreOutlined,
  PlusOutlined,
  ColumnHeightOutlined,
  TagsOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Controller, useForm, useWatch } from 'react-hook-form';
import type { Control, Path } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useDebounce, useRequest } from 'ahooks';

import WizardShell from '@/components/common/wizard/WizardShell';
import { useFeatureFlags } from '@/features/flags/FeatureFlagContext';
import { usePricingDataset } from '@/hooks/usePricing';
import type { WizardShellTexts, WizardStepItem } from '@/components/common/wizard/WizardShell';
import { FormFieldWrapper } from '@/components/common/form/FormFieldWrapper';
import { FormSection } from '@/components/common/form/FormSection';
import TaxonomyCascader from '@/components/common/TaxonomyCascader';
import ReadinessMeter from '@/components/common/ReadinessMeter';
import type { ReadinessMeterItem } from '@/components/common/ReadinessMeter';
import BlockedFieldCallout from '@/components/common/BlockedFieldCallout';
import TableContainer, { renderDash } from '@/components/common/TableContainer';
import PurpleTag from '@/components/common/PurpleTag';
import CreateNodeModal from '@/components/taxonomy/CreateNodeModal';
import { useUnsavedChangesModal } from '@/components/common/ConfirmModal';
import ProductImageGallery from '@/components/common/ProductImageGallery';
import type { PendingImageTile } from '@/components/common/ProductImageGallery';
import ScnLinkDrawer from './ProductDetailPage/ScnLinkDrawer';
import { linkableOemMatches, useOemDupCheck } from './useOemDupCheck';
import ImageSlotModal from './ImageSlotModal';
import { productSectionDeepLink } from './ProductDetailPage/types';
import { imageUploadDemoAllowed } from '@/config/featureFlags';
import {
  ACCEPTED_IMAGE_ACCEPT,
  readAndDownscaleImage,
  validateImageFile,
} from '@/utils/imageFileUtils';

import { useWizardStyles } from '@/styles/wizardStyles';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import { productApi, customerApi } from '@/services/api';
import { vendorLineWriteReachesTheRead } from '@/services/api/product.api';
import { invalidateProductCaches, useProduct } from '@/hooks/useProducts';
import { useVendorOptions } from '@/hooks/useVendors';
import { useTaxonomyTree } from '@/hooks/useTaxonomy';
import { computeProductReadiness, getRulesBySection } from '@/config/productFieldRegistry';
import { computeAgeGroup } from '@/lib/products/computeAgeGroup';
import { MAX_PRODUCT_IMAGES, PRODUCT_WEIGHT_UNITS } from '@/types/api/product';
import type {
  CheckOemMatch,
  CheckOemResponse,
  CheckSkuResponse,
  ProductNote,
  ProductNoteAudience,
  ProductReplenishmentPlanning,
  ProductWeightUnit,
} from '@/types/api/product';
import {
  PRODUCT_WIZARD_STEP_FIELDS,
  UNIT_VALUES,
  customerPartNumberRowSchema,
  fitmentYearRowSchema,
  hydrateFromProduct,
  productWizardDefaults,
  productWizardSchema,
  sparePartNumberRowSchema,
  toCreateProductRequest,
  toReadinessPreview,
  toUpdateProductRequest,
  vendorLineRowSchema,
} from '@/schemas/forms/productFormSchemas';
import type {
  CustomerPartNumberRow,
  FitmentYearRow,
  ImageSlotRow,
  ProductWizardFormData,
  SparePartNumberRow,
  VendorLineRow,
} from '@/schemas/forms/productFormSchemas';

const { Text } = Typography;

// ---------------------------------------------------------------------------
// Local option constants (no reference endpoints exist yet — see followUps)
// ---------------------------------------------------------------------------

const SUBSIDIARY_OPTIONS = [
  { value: 'SUB-TW-HOLIEN', labelKey: 'products:wizard.subsidiaries.tw' },
  { value: 'SUB-AU-SENDIT', labelKey: 'products:wizard.subsidiaries.au' },
] as const;

// UNIT_VALUES moved to productFormSchemas (W7-B3) — shared with the
// vendor-line 訂購單位 capture in AddVendorLineModal.
const CURRENCY_VALUES = ['AUD', 'USD', 'TWD', 'CNY', 'EUR'] as const;
const COSTING_METHOD_VALUES = ['FIFO', 'MOVING_AVERAGE', 'STANDARD', 'WEIGHTED_AVERAGE'] as const;
// DD!69 FA Sub Category (S2.1) — aligned with the S1.1 narrative seed values;
// no reference endpoint exists yet (see followUps).
const FA_SUB_CATEGORY_VALUES = [
  'Body Plastics',
  'Brake Friction',
  'Brake Hydraulics',
  'Brake Rotor',
  'Cooling',
  'Lighting',
  'Suspension',
] as const;
const NOTE_AUDIENCES: readonly ProductNoteAudience[] = ['all', 'procurement', 'sales', 'finance'];

const MAKE_SUGGESTIONS = [
  'TOYOTA',
  'FORD',
  'MAZDA',
  'NISSAN',
  'MITSUBISHI',
  'ISUZU',
  'HONDA',
  'HYUNDAI',
  'KIA',
  'HOLDEN',
];
const MODEL_SUGGESTIONS = ['HILUX', 'RANGER', 'TRITON', 'D-MAX', 'NAVARA', 'BT-50', 'COLORADO'];

// ---------------------------------------------------------------------------
// Module-level controlled fields (stable identity — no focus loss) that
// translate zod message KEYS at display time.
// ---------------------------------------------------------------------------

interface BaseFieldProps {
  name: Path<ProductWizardFormData>;
  control: Control<ProductWizardFormData>;
  label: string;
  required?: boolean;
  placeholder?: string;
  help?: string;
  disabled?: boolean;
}

const WizardTextField = ({
  name,
  control,
  label,
  required,
  placeholder,
  help,
  disabled,
}: BaseFieldProps) => {
  const { t } = useTranslation(['products', 'common']);
  const styles = useWizardStyles();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <FormFieldWrapper
          label={label}
          required={required}
          error={error ? t(error.message ?? '') : undefined}
          help={help}
        >
          <Input
            {...field}
            value={(field.value as string | undefined) ?? ''}
            size="large"
            placeholder={placeholder}
            disabled={disabled}
            style={error ? styles.formInputError : styles.formInput}
          />
        </FormFieldWrapper>
      )}
    />
  );
};

interface NumberFieldProps extends BaseFieldProps {
  min?: number;
  max?: number;
  precision?: number;
}

const WizardNumberField = ({
  name,
  control,
  label,
  required,
  placeholder,
  help,
  disabled,
  min,
  max,
  precision,
}: NumberFieldProps) => {
  const { t } = useTranslation(['products', 'common']);
  const styles = useWizardStyles();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <FormFieldWrapper
          label={label}
          required={required}
          error={error ? t(error.message ?? '') : undefined}
          help={help}
        >
          <InputNumber
            size="large"
            placeholder={placeholder}
            disabled={disabled}
            min={min}
            max={max}
            precision={precision}
            value={(field.value as number | null | undefined) ?? null}
            onChange={(value) => field.onChange(value ?? null)}
            onBlur={field.onBlur}
            style={error ? styles.formInputError : styles.formInput}
            status={error ? 'error' : undefined}
          />
        </FormFieldWrapper>
      )}
    />
  );
};

interface NumberWithUnitFieldProps extends NumberFieldProps {
  /** Schema path of the companion unit qualifier (DD!98/100/102 pattern). */
  unitName: Path<ProductWizardFormData>;
  unitAriaLabel: string;
  unitOptions: { label: string; value: string }[];
}

/**
 * Number + unit-qualifier compact pair (S2.1) — the weightKg↔weightUnit
 * pattern, but with BOTH halves in the zod schema (S0.3 drift-class guard).
 */
const WizardNumberWithUnitField = ({
  name,
  control,
  label,
  required,
  placeholder,
  help,
  disabled,
  min,
  max,
  precision,
  unitName,
  unitAriaLabel,
  unitOptions,
}: NumberWithUnitFieldProps) => {
  const { t } = useTranslation(['products', 'common']);
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <FormFieldWrapper
          label={label}
          required={required}
          error={error ? t(error.message ?? '') : undefined}
          help={help}
        >
          <Space.Compact style={{ width: '100%' }}>
            <InputNumber
              size="large"
              placeholder={placeholder}
              disabled={disabled}
              min={min}
              max={max}
              precision={precision}
              value={(field.value as number | null | undefined) ?? null}
              onChange={(value) => field.onChange(value ?? null)}
              onBlur={field.onBlur}
              status={error ? 'error' : undefined}
              style={{ flex: 1 }}
            />
            <Controller
              name={unitName}
              control={control}
              render={({ field: unitField }) => (
                <Select
                  size="large"
                  allowClear
                  aria-label={unitAriaLabel}
                  disabled={disabled}
                  value={((unitField.value as string | undefined) || undefined) ?? undefined}
                  options={unitOptions}
                  onChange={(value) => unitField.onChange(value ?? '')}
                  onBlur={unitField.onBlur}
                  style={{ width: 96 }}
                />
              )}
            />
          </Space.Compact>
        </FormFieldWrapper>
      )}
    />
  );
};

interface SelectFieldProps extends BaseFieldProps {
  options: { label: string; value: string }[];
  mode?: 'multiple' | 'tags';
  allowClear?: boolean;
  showSearch?: boolean;
  /** Extra hook after value change (e.g. trigger() to clear Select errors). */
  onValueChange?: (value: unknown) => void;
}

const WizardSelectField = ({
  name,
  control,
  label,
  required,
  placeholder,
  help,
  disabled,
  options,
  mode,
  allowClear = false,
  showSearch = false,
  onValueChange,
}: SelectFieldProps) => {
  const { t } = useTranslation(['products', 'common']);
  const styles = useWizardStyles();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <FormFieldWrapper
          label={label}
          required={required}
          error={error ? t(error.message ?? '') : undefined}
          help={help}
        >
          <Select
            size="large"
            mode={mode}
            placeholder={placeholder}
            disabled={disabled}
            allowClear={allowClear}
            showSearch={showSearch}
            optionFilterProp="label"
            options={options}
            value={(field.value as string | string[] | null | undefined) ?? undefined}
            status={error ? 'error' : undefined}
            style={styles.formInput}
            onChange={(value) => {
              field.onChange(value ?? (mode ? [] : null));
              onValueChange?.(value);
            }}
            onBlur={field.onBlur}
          />
        </FormFieldWrapper>
      )}
    />
  );
};

interface TextAreaFieldProps extends BaseFieldProps {
  rows?: number;
  onValueChange?: (value: string) => void;
}

const WizardTextAreaField = ({
  name,
  control,
  label,
  required,
  placeholder,
  help,
  disabled,
  rows = 3,
  onValueChange,
}: TextAreaFieldProps) => {
  const { t } = useTranslation(['products', 'common']);
  const styles = useWizardStyles();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <FormFieldWrapper
          label={label}
          required={required}
          error={error ? t(error.message ?? '') : undefined}
          help={help}
        >
          <Input.TextArea
            {...field}
            value={(field.value as string | undefined) ?? ''}
            rows={rows}
            placeholder={placeholder}
            disabled={disabled}
            style={{ ...styles.formInput, height: 'auto' }}
            onChange={(event) => {
              field.onChange(event);
              onValueChange?.(event.target.value);
            }}
          />
        </FormFieldWrapper>
      )}
    />
  );
};

// ---------------------------------------------------------------------------
// SKU dup-check FSM (ABN-check pattern: debounce 800ms + reducer)
// ---------------------------------------------------------------------------

type SkuConflict = NonNullable<CheckSkuResponse['conflict']>;

interface SkuCheckState {
  status: 'IDLE' | 'VALIDATING' | 'AVAILABLE' | 'TAKEN';
  sku: string | null;
  conflict: SkuConflict | null;
}

type SkuCheckAction =
  | { type: 'TYPING' }
  | { type: 'START'; sku: string }
  | { type: 'AVAILABLE'; sku: string }
  | { type: 'TAKEN'; sku: string; conflict: SkuConflict | null }
  | { type: 'RESET' };

const skuCheckReducer = (state: SkuCheckState, action: SkuCheckAction): SkuCheckState => {
  switch (action.type) {
    case 'TYPING':
      return { status: 'IDLE', sku: null, conflict: null };
    case 'START':
      return { status: 'VALIDATING', sku: action.sku, conflict: null };
    case 'AVAILABLE':
      // Ignore stale responses for a different SKU
      if (state.status === 'VALIDATING' && state.sku !== action.sku) return state;
      return { status: 'AVAILABLE', sku: action.sku, conflict: null };
    case 'TAKEN':
      if (state.status === 'VALIDATING' && state.sku !== action.sku) return state;
      return { status: 'TAKEN', sku: action.sku, conflict: action.conflict };
    case 'RESET':
      return { status: 'IDLE', sku: null, conflict: null };
    default:
      return state;
  }
};

// ---------------------------------------------------------------------------
// OEM dup-check (S7.4) — model + debounced lookup HOISTED to the shared
// `useOemDupCheck` (FIELD-SIGNOFF ROW 22 / F4 §2-Fix5: the classification
// chunk edit runs the same advisory check). Re-exported so the model-layer
// vitest (oemDupCheck.test.tsx) and existing consumers keep their paths.
// ---------------------------------------------------------------------------

/* eslint-disable react-refresh/only-export-components -- the OEM dup-check
   model (re-exported from the shared hook) + carry/decline helpers are
   exported for the model-layer vitest (oemDupCheck.test.tsx), matching the
   ItemLocationsSection precedent: the house test env is plain node, so
   behavior is proven on the EXACT exported functions the wizard renders with. */

export { linkableOemMatches, oemCheckReducer } from './useOemDupCheck';
export type { OemCheckAction, OemCheckState } from './useOemDupCheck';

/**
 * FOLLOW-UP DISCHARGED 2026-08-10. Both this file and the domain rule carried
 * the same note — "hoist into `src/types/api/product.ts` + `productApi.checkOem`
 * once that file is free". Done: the shape lives in `@/types/api/product` and
 * the typed leg is `productApi.checkOem`, so the wizard no longer re-exports a
 * hand-copied structural twin. The two names stay exported because the FSM,
 * `linkableOemMatches` and the matches Table read better in the wizard's own
 * vocabulary — they are now ALIASES of the typed-API shapes, not a second
 * declaration of them. (The debounced lookup itself moved to the shared
 * `useOemDupCheck` seat; repointing that hook's transport at
 * `productApi.checkOem` is the remaining half of the same discharge.)
 */
export type OemDupMatch = CheckOemMatch;
export type CheckOemClientResponse = CheckOemResponse;

/**
 * Post-create retroactive SCN carry (系統自動帶入 SCN). SCN is NEVER on the
 * create payload (fixed decision) — the wizard PATCHes it right after create.
 * Declining the prompt leaves `pendingScn` null ⇒ no PATCH ⇒ SCN stays blank
 * (consistent with required-iff-共用).
 */
export const buildPostCreateScnPatch = (pendingScn: string | null): { scn: string } | null =>
  pendingScn ? { scn: pendingScn } : null;

/* eslint-enable react-refresh/only-export-components */

// ---------------------------------------------------------------------------
// Row modals (fitment years / customer PNs / vendor lines / images)
// ---------------------------------------------------------------------------

interface RowModalBaseProps {
  open: boolean;
  onClose: () => void;
}

interface FitmentYearModalProps extends RowModalBaseProps {
  initial: FitmentYearRow | null;
  onSave: (row: FitmentYearRow) => void;
}

const FitmentYearModal = ({ open, initial, onClose, onSave }: FitmentYearModalProps) => {
  const { t } = useTranslation(['products', 'common']);
  const { token } = theme.useToken();
  const formStyles = useFormStyles();
  const isEdit = initial !== null;

  const emptyRow = useMemo(
    () =>
      ({
        id: `year-${Date.now()}`,
        yearFrom: null,
        yearTo: null,
        variant: '',
      }) as unknown as FitmentYearRow,
    // Regenerate the row id each time the modal opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open]
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FitmentYearRow>({
    resolver: zodResolver(fitmentYearRowSchema),
    defaultValues: emptyRow,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (open) reset(initial ?? emptyRow);
  }, [open, initial, emptyRow, reset]);

  const fieldError = (name: keyof FitmentYearRow): ReactNode =>
    errors[name] ? (
      <Text style={formStyles.errorStyle}>{t(errors[name]?.message ?? '')}</Text>
    ) : null;

  return (
    <Modal
      open={open}
      title={isEdit ? t('products:wizard.modals.editYear') : t('products:wizard.modals.addYear')}
      okText={isEdit ? t('common:actions.save') : t('common:actions.add')}
      cancelText={t('common:actions.cancel')}
      onCancel={onClose}
      onOk={handleSubmit((row) => {
        onSave(row);
        onClose();
      })}
      okButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
      cancelButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
      destroyOnHidden
    >
      <div style={formStyles.fieldContainerStyle}>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Text style={formStyles.labelStyle}>
              <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
              {t('products:fields.yearFrom')}
            </Text>
            <Controller
              name="yearFrom"
              control={control}
              render={({ field }) => (
                <InputNumber
                  size="large"
                  min={1950}
                  max={2100}
                  precision={0}
                  value={(field.value as number | null) ?? null}
                  onChange={(value) => field.onChange(value ?? null)}
                  onBlur={field.onBlur}
                  style={formStyles.inputStyle}
                  status={errors.yearFrom ? 'error' : undefined}
                />
              )}
            />
            {fieldError('yearFrom')}
          </Col>
          <Col span={12}>
            <Text style={formStyles.labelStyle}>{t('products:fields.yearTo')}</Text>
            <Controller
              name="yearTo"
              control={control}
              render={({ field }) => (
                <InputNumber
                  size="large"
                  min={1950}
                  max={2100}
                  precision={0}
                  value={(field.value as number | null) ?? null}
                  onChange={(value) => field.onChange(value ?? null)}
                  onBlur={field.onBlur}
                  style={formStyles.inputStyle}
                  status={errors.yearTo ? 'error' : undefined}
                />
              )}
            />
            {fieldError('yearTo')}
          </Col>
        </Row>
        <Text style={{ ...formStyles.labelStyle, marginTop: '16px' }}>
          {t('products:fields.variant')}
        </Text>
        <Controller
          name="variant"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              value={field.value ?? ''}
              size="large"
              style={formStyles.inputStyle}
            />
          )}
        />
      </div>
    </Modal>
  );
};

interface CustomerPartNumberModalProps extends RowModalBaseProps {
  initial: CustomerPartNumberRow | null;
  customerOptions: { label: string; value: number }[];
  customersLoading: boolean;
  onSave: (row: CustomerPartNumberRow) => void;
}

const CustomerPartNumberModal = ({
  open,
  initial,
  customerOptions,
  customersLoading,
  onClose,
  onSave,
}: CustomerPartNumberModalProps) => {
  const { t } = useTranslation(['products', 'common']);
  const { token } = theme.useToken();
  const formStyles = useFormStyles();
  const isEdit = initial !== null;

  const emptyRow = useMemo(
    () =>
      ({
        id: `pn-${Date.now()}`,
        customerId: null,
        customerName: '',
        partNumber: '',
        description: '',
      }) as unknown as CustomerPartNumberRow,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open]
  );

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<CustomerPartNumberRow>({
    resolver: zodResolver(customerPartNumberRowSchema),
    defaultValues: emptyRow,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (open) reset(initial ?? emptyRow);
  }, [open, initial, emptyRow, reset]);

  const fieldError = (name: keyof CustomerPartNumberRow): ReactNode =>
    errors[name] ? (
      <Text style={formStyles.errorStyle}>{t(errors[name]?.message ?? '')}</Text>
    ) : null;

  return (
    <Modal
      open={open}
      title={isEdit ? t('products:wizard.modals.editPn') : t('products:wizard.modals.addPn')}
      okText={isEdit ? t('common:actions.save') : t('common:actions.add')}
      cancelText={t('common:actions.cancel')}
      onCancel={onClose}
      onOk={handleSubmit((row) => {
        onSave(row);
        onClose();
      })}
      okButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
      cancelButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
      destroyOnHidden
    >
      <div style={formStyles.fieldContainerStyle}>
        <Text style={formStyles.labelStyle}>
          <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
          {t('products:fields.customer')}
        </Text>
        <Controller
          name="customerId"
          control={control}
          render={({ field }) => (
            <Select
              size="large"
              showSearch
              optionFilterProp="label"
              loading={customersLoading}
              options={customerOptions}
              value={(field.value as number | null) ?? undefined}
              status={errors.customerId ? 'error' : undefined}
              style={formStyles.inputStyle}
              onChange={(value) => {
                field.onChange(value ?? null);
                const label = customerOptions.find((option) => option.value === value)?.label;
                setValue('customerName', label ?? '');
              }}
              onBlur={field.onBlur}
            />
          )}
        />
        {fieldError('customerId')}

        <Text style={{ ...formStyles.labelStyle, marginTop: '16px' }}>
          <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
          {t('products:fields.partNumber')}
        </Text>
        <Controller
          name="partNumber"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              size="large"
              style={formStyles.inputStyle}
              status={errors.partNumber ? 'error' : undefined}
            />
          )}
        />
        {fieldError('partNumber')}

        <Text style={{ ...formStyles.labelStyle, marginTop: '16px' }}>
          {t('products:fields.description')}
        </Text>
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              value={field.value ?? ''}
              size="large"
              style={formStyles.inputStyle}
            />
          )}
        />
      </div>
    </Modal>
  );
};

interface SparePartNumberModalProps extends RowModalBaseProps {
  initial: SparePartNumberRow | null;
  onSave: (row: SparePartNumberRow) => void;
}

// 備件料號 row modal (2026-08-27 §5) — mirror of CustomerPartNumberModal with
// NO party select: the spare-part alias belongs to the product itself.
const SparePartNumberModal = ({ open, initial, onClose, onSave }: SparePartNumberModalProps) => {
  const { t } = useTranslation(['products', 'common']);
  const { token } = theme.useToken();
  const formStyles = useFormStyles();
  const isEdit = initial !== null;

  const emptyRow = useMemo(
    (): SparePartNumberRow => ({
      id: `spn-${Date.now()}`,
      partNumber: '',
      description: '',
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open]
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SparePartNumberRow>({
    resolver: zodResolver(sparePartNumberRowSchema),
    defaultValues: emptyRow,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (open) reset(initial ?? emptyRow);
  }, [open, initial, emptyRow, reset]);

  const fieldError = (name: keyof SparePartNumberRow): ReactNode =>
    errors[name] ? (
      <Text style={formStyles.errorStyle}>{t(errors[name]?.message ?? '')}</Text>
    ) : null;

  return (
    <Modal
      open={open}
      title={
        isEdit ? t('products:wizard.modals.editSparePn') : t('products:wizard.modals.addSparePn')
      }
      okText={isEdit ? t('common:actions.save') : t('common:actions.add')}
      cancelText={t('common:actions.cancel')}
      onCancel={onClose}
      onOk={handleSubmit((row) => {
        onSave(row);
        onClose();
      })}
      okButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
      cancelButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
      destroyOnHidden
    >
      <div style={formStyles.fieldContainerStyle}>
        <Text style={formStyles.labelStyle}>
          <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
          {t('products:fields.partNumber')}
        </Text>
        <Controller
          name="partNumber"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              size="large"
              style={formStyles.inputStyle}
              status={errors.partNumber ? 'error' : undefined}
            />
          )}
        />
        {fieldError('partNumber')}

        <Text style={{ ...formStyles.labelStyle, marginTop: '16px' }}>
          {t('products:fields.description')}
        </Text>
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              value={field.value ?? ''}
              size="large"
              style={formStyles.inputStyle}
            />
          )}
        />
      </div>
    </Modal>
  );
};

interface VendorLineModalProps extends RowModalBaseProps {
  initial: VendorLineRow | null;
  vendorOptions: { label: string; value: number }[];
  vendorsLoading: boolean;
  /** True when the product has no primary line yet — forces the first line primary. */
  forcePrimary: boolean;
  onSave: (row: VendorLineRow) => void;
}

const VendorLineModal = ({
  open,
  initial,
  vendorOptions,
  vendorsLoading,
  forcePrimary,
  onClose,
  onSave,
}: VendorLineModalProps) => {
  const { t } = useTranslation(['products', 'common']);
  const { token } = theme.useToken();
  const formStyles = useFormStyles();
  const isEdit = initial !== null;

  const emptyRow = useMemo(
    () =>
      ({
        id: `pvl-${Date.now()}`,
        vendorId: null,
        vendorName: '',
        vendorSku: '',
        unitCost: null,
        currency: 'USD',
        orderUnit: null,
        isPrimary: forcePrimary,
        leadTimeDays: null,
        isExisting: false,
      }) as unknown as VendorLineRow,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open, forcePrimary]
  );

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<VendorLineRow>({
    resolver: zodResolver(vendorLineRowSchema),
    defaultValues: emptyRow,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (open) reset(initial ?? emptyRow);
  }, [open, initial, emptyRow, reset]);

  const fieldError = (name: keyof VendorLineRow): ReactNode =>
    errors[name] ? (
      <Text style={formStyles.errorStyle}>{t(errors[name]?.message ?? '')}</Text>
    ) : null;

  return (
    <Modal
      open={open}
      title={
        isEdit ? t('products:wizard.modals.editVendor') : t('products:wizard.modals.addVendor')
      }
      okText={isEdit ? t('common:actions.save') : t('common:actions.add')}
      cancelText={t('common:actions.cancel')}
      onCancel={onClose}
      onOk={handleSubmit((row) => {
        onSave(row);
        onClose();
      })}
      okButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
      cancelButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
      destroyOnHidden
    >
      <div style={formStyles.fieldContainerStyle}>
        <Text style={formStyles.labelStyle}>
          <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
          {t('products:fields.vendor')}
        </Text>
        <Controller
          name="vendorId"
          control={control}
          render={({ field }) => (
            <Select
              size="large"
              showSearch
              optionFilterProp="label"
              loading={vendorsLoading}
              options={vendorOptions}
              value={(field.value as number | null) ?? undefined}
              status={errors.vendorId ? 'error' : undefined}
              style={formStyles.inputStyle}
              onChange={(value) => {
                field.onChange(value ?? null);
                const label = vendorOptions.find((option) => option.value === value)?.label;
                setValue('vendorName', label ?? '');
              }}
              onBlur={field.onBlur}
            />
          )}
        />
        {fieldError('vendorId')}

        <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
          <Col span={12}>
            <Text style={formStyles.labelStyle}>{t('products:fields.vendorSku')}</Text>
            <Controller
              name="vendorSku"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  value={field.value ?? ''}
                  size="large"
                  style={formStyles.inputStyle}
                />
              )}
            />
          </Col>
          <Col span={12}>
            <Text style={formStyles.labelStyle}>{t('products:fields.leadTimeDays')}</Text>
            <Controller
              name="leadTimeDays"
              control={control}
              render={({ field }) => (
                <InputNumber
                  size="large"
                  min={0}
                  precision={0}
                  value={(field.value as number | null) ?? null}
                  onChange={(value) => field.onChange(value ?? null)}
                  onBlur={field.onBlur}
                  style={formStyles.inputStyle}
                />
              )}
            />
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
          <Col span={8}>
            <Text style={formStyles.labelStyle}>
              <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
              {t('products:fields.unitCost')}
            </Text>
            <Controller
              name="unitCost"
              control={control}
              render={({ field }) => (
                <InputNumber
                  size="large"
                  min={0}
                  value={(field.value as number | null) ?? null}
                  onChange={(value) => field.onChange(value ?? null)}
                  onBlur={field.onBlur}
                  style={formStyles.inputStyle}
                  status={errors.unitCost ? 'error' : undefined}
                />
              )}
            />
            {fieldError('unitCost')}
          </Col>
          <Col span={8}>
            <Text style={formStyles.labelStyle}>
              <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
              {t('products:fields.currency')}
            </Text>
            <Controller
              name="currency"
              control={control}
              render={({ field }) => (
                <Select
                  size="large"
                  options={CURRENCY_VALUES.map((code) => ({ label: code, value: code }))}
                  value={field.value || undefined}
                  status={errors.currency ? 'error' : undefined}
                  style={formStyles.inputStyle}
                  onChange={(value) => field.onChange(value ?? '')}
                  onBlur={field.onBlur}
                />
              )}
            />
            {fieldError('currency')}
          </Col>
          <Col span={8}>
            {/* W7-B3 訂購單位 — optional capture; NO qty conversion (Q5). */}
            <Text style={formStyles.labelStyle}>{t('products:fields.orderUnit')}</Text>
            <Controller
              name="orderUnit"
              control={control}
              render={({ field }) => (
                <Select
                  size="large"
                  allowClear
                  options={UNIT_VALUES.map((unit) => ({ label: unit, value: unit }))}
                  value={(field.value as string | null) ?? undefined}
                  style={formStyles.inputStyle}
                  onChange={(value) => field.onChange(value ?? null)}
                  onBlur={field.onBlur}
                />
              )}
            />
          </Col>
        </Row>

        <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Controller
            name="isPrimary"
            control={control}
            render={({ field }) => (
              <Switch
                checked={field.value}
                disabled={forcePrimary && !isEdit}
                onChange={(checked) => field.onChange(checked)}
              />
            )}
          />
          <Text>{t('products:preferred')}</Text>
        </div>
      </div>
    </Modal>
  );
};

// ImageSlotModal (URL entry) extracted to ./ImageSlotModal in V3 — shared
// with the detail Images section; imported at the top of this file.

// ---------------------------------------------------------------------------
// Wizard page
// ---------------------------------------------------------------------------

/**
 * G-2169-SUITE-01 render probe (test seam only — the route mounts the wizard
 * with NO props, so production behavior is byte-identical): the SHOW-on-create
 * suite (`__tests__/mustShowOnCreate.test.tsx`) runs in the plain-node vitest
 * env (no jsdom — see vitest.config.ts) and proves each Must label's create
 * slot by SSR-rendering one step at a time. Since #2172 D-A, CREATE mode
 * paints the step-④ 會計 Collapse expanded on first paint (the 10 DD!217–226
 * accounting Musts are SHOW-OPEN), so the suite proves them WITHOUT
 * `expandAccounting`. The probe stays for EDIT mode, whose default remains
 * collapsed (per the Q-M3-adjacent collapsed-by-default ruling).
 */
export interface ProductFormWizardShowProbe {
  /** Zero-based step to mount on (default 0). */
  initialStep?: number;
  /** Force the step-④ accounting Collapse expanded (default false). */
  expandAccounting?: boolean;
}

interface ProductFormWizardProps {
  showProbe?: ProductFormWizardShowProbe;
}

const ProductFormWizard = ({ showProbe }: ProductFormWizardProps) => {
  const { t } = useTranslation(['products', 'common']);
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const { productId } = useParams<{ productId: string }>();
  const isEditMode = Boolean(productId);
  const { message, modal, notification } = App.useApp();
  const styles = useWizardStyles();
  const { confirmUnsavedChanges } = useUnsavedChangesModal();

  const [currentStep, setCurrentStep] = useState(showProbe?.initialStep ?? 0);
  const [stepErrors, setStepErrors] = useState<Record<number, boolean>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [conflictModalOpen, setConflictModalOpen] = useState(false);
  const [createNodeParentId, setCreateNodeParentId] = useState<string | null>(null);
  const [createNodeOpen, setCreateNodeOpen] = useState(false);

  // -- S7.4 OEM dup-detection surfaces (advisory — never block create/save) --
  const [oemPromptOpen, setOemPromptOpen] = useState(false);
  /** Selection inside the prompt (create mode 選擇式關聯). */
  const [oemPromptScn, setOemPromptScn] = useState<string | undefined>(undefined);
  /** Accepted carry — retroactive PATCH right after create (系統自動帶入 SCN). */
  const [pendingScn, setPendingScn] = useState<string | null>(null);
  /** Cyan board case OEM不同但確定共用 — pending-review badge ONLY (Q-O3). */
  const [cyanPendingReview, setCyanPendingReview] = useState(false);
  /** Edit mode routes 選擇式關聯 through the S7.3 ScnLinkDrawer. */
  const [scnDrawerOpen, setScnDrawerOpen] = useState(false);
  const promptedOemRef = useRef<string | null>(null);

  // Row modal state
  const [yearModal, setYearModal] = useState<{ open: boolean; row: FitmentYearRow | null }>({
    open: false,
    row: null,
  });
  const [pnModal, setPnModal] = useState<{ open: boolean; row: CustomerPartNumberRow | null }>({
    open: false,
    row: null,
  });
  const [sparePnModal, setSparePnModal] = useState<{
    open: boolean;
    row: SparePartNumberRow | null;
  }>({ open: false, row: null });
  const [vendorModal, setVendorModal] = useState<{ open: boolean; row: VendorLineRow | null }>({
    open: false,
    row: null,
  });
  const [imageModal, setImageModal] = useState<{ open: boolean; row: ImageSlotRow | null }>({
    open: false,
    row: null,
  });

  // -- 0708-J alignment fields — local state (kept OUT of the zod schema so
  //    the shared productFormSchemas contract stays untouched; merged into the
  //    create/update request at submit time). The S2.1 fields (variant /
  //    extractYear / faSubCategory / age years / unit qualifiers) are NOT here:
  //    they live in the zod schema per the S0.3 drift-class guard. `ageGroup`
  //    is system-computed (C108) — no state, no direct entry, never sent. -----
  const [manufacturer, setManufacturer] = useState('');
  const [weightUnit, setWeightUnit] = useState<ProductWeightUnit>('kg');
  const [productNotes, setProductNotes] = useState<ProductNote[]>([]);
  const [damageStockNotes, setDamageStockNotes] = useState('');
  const [itemComment, setItemComment] = useState('');
  const [noteDraft, setNoteDraft] = useState<{ text: string; audience: ProductNoteAudience }>({
    text: '',
    audience: 'all',
  });

  // -- SR0.2 replenishment planning (DD!53–67, SHOULD-tier) — local state,
  //    kept OUT of the shared zod schema (like the 0708-J fields above); merged
  //    into the create/update body at submit. NEVER create-required / gating.
  const [planning, setPlanning] = useState<ProductReplenishmentPlanning>({});
  const setPlanningField = <K extends keyof ProductReplenishmentPlanning>(
    key: K,
    value: ProductReplenishmentPlanning[K]
  ) => setPlanning((prev) => ({ ...prev, [key]: value }));

  /** Collapse the block to `undefined` when nothing meaningful is set. */
  const buildPlanning = (): ProductReplenishmentPlanning | undefined => {
    const hasValue = Object.values(planning).some(
      (value) => value === true || (typeof value === 'number' && Number.isFinite(value))
    );
    return hasValue ? planning : undefined;
  };

  const addProductNote = () => {
    const text = noteDraft.text.trim();
    if (!text) return;
    setProductNotes((notes) => [
      ...notes,
      {
        id: `note-${Date.now()}`,
        text,
        audience: noteDraft.audience,
        createdAt: new Date().toISOString(),
      },
    ]);
    setNoteDraft({ text: '', audience: noteDraft.audience });
  };

  const removeProductNote = (id: string) =>
    setProductNotes((notes) => notes.filter((note) => note.id !== id));

  /** 0708-J + SR0.2 planning fields merged into every create/update body. */
  const alignmentFields = () => ({
    manufacturer: manufacturer.trim() || undefined,
    weightUnit,
    productNotes,
    damageStockNotes: damageStockNotes.trim() || undefined,
    itemComment: itemComment.trim() || undefined,
    planning: buildPlanning(),
  });

  const customsEditedRef = useRef(false);
  const serverPrimaryVendorRef = useRef<number | null>(null);
  const hydratedRef = useRef(false);

  // -- Form -----------------------------------------------------------------
  const {
    control,
    trigger,
    reset,
    getValues,
    setValue,
    setError,
    clearErrors,
    setFocus,
    formState: { isDirty, errors },
  } = useForm<ProductWizardFormData>({
    resolver: zodResolver(productWizardSchema),
    defaultValues: productWizardDefaults,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  // D17 (flag-gated) — CREATE starts from the company costing-method default
  // (Melissa 2026-08-28: "set those moving average things … then carry it as
  // default"). Fires once, only while the field is still empty — a user pick
  // (or edit-mode hydrate) is never overwritten; flag-off leaves the wizard
  // byte-identical.
  const { isEnabled } = useFeatureFlags();
  const { dataset: pricingDefaultsDataset } = usePricingDataset();
  const methodPreselectedRef = useRef(false);
  const companyCostingMethodDefault = pricingDefaultsDataset?.costingMethodDefault;
  useEffect(() => {
    if (isEditMode || methodPreselectedRef.current) return;
    if (!isEnabled('engines.pricing')) return;
    if (!companyCostingMethodDefault) return;
    if (getValues('costingMethod') != null) return;
    setValue('costingMethod', companyCostingMethodDefault);
    methodPreselectedRef.current = true;
  }, [isEditMode, isEnabled, companyCostingMethodDefault, getValues, setValue]);

  const pricingOn = isEnabled('engines.pricing');
  // RULING-2 (2026-08-29, flag-gated): currency is system-set — step ④
  // renders the W6 read-only box instead of the Select, and the create/update
  // bodies omit the field (toCreateProductRequest option). Locking edit-only
  // would FOSSILIZE non-system currencies, so create locks too; flag-off
  // keeps the pickable Select + sent field byte-identically.
  const currencySystemSet = pricingOn;
  const formStyles = useFormStyles();
  // Display value for the read-only box: the hydrated `product.currency` in
  // edit mode / the 'AUD' schema default in create (productFormSchemas).
  const watchedCurrencyValue = useWatch({ control, name: 'currency' });

  // -- Data -----------------------------------------------------------------
  const {
    data: existingResponse,
    loading: productLoading,
    refresh: refreshExistingProduct,
  } = useProduct(
    isEditMode ? productId : undefined
  );
  const existingProduct = existingResponse?.data;

  const { nodes: taxonomyNodes } = useTaxonomyTree();

  const { vendors, isLoading: vendorsLoading } = useVendorOptions();
  const vendorOptions = useMemo(
    () =>
      vendors.map((vendor) => ({
        label: vendor.vendorName,
        value: vendor.vendorId,
      })),
    [vendors]
  );

  const { data: customersResponse, loading: customersLoading } = useRequest(
    () => customerApi.list({ isActive: 1, pageSize: -1 }),
    { cacheKey: '/api/customers-options', staleTime: 60000 }
  );
  const customerOptions = useMemo(
    () =>
      (customersResponse?.data ?? []).map((customer) => ({
        label: customer.customerName,
        value: customer.customerId,
      })),
    [customersResponse]
  );

  // -- Edit hydration -------------------------------------------------------
  useEffect(() => {
    hydratedRef.current = false;
  }, [productId]);

  useEffect(() => {
    if (isEditMode && existingProduct && !hydratedRef.current) {
      reset(hydrateFromProduct(existingProduct));
      // 0708-J alignment fields (local state, outside the shared schema)
      setManufacturer(existingProduct.manufacturer ?? '');
      setWeightUnit(existingProduct.weightUnit ?? 'kg');
      setProductNotes(existingProduct.productNotes ?? []);
      setDamageStockNotes(existingProduct.damageStockNotes ?? '');
      setItemComment(existingProduct.itemComment ?? '');
      setPlanning(existingProduct.planning ?? {});
      customsEditedRef.current = Boolean(
        existingProduct.customsDescription &&
          existingProduct.customsDescription !== existingProduct.displayName
      );
      serverPrimaryVendorRef.current =
        existingProduct.vendorLines?.find((line) => line.isPrimary)?.vendorId ?? null;
      hydratedRef.current = true;
    }
  }, [isEditMode, existingProduct, reset]);

  // -- SKU dup-check FSM ----------------------------------------------------
  const [skuCheck, dispatchSku] = useReducer(skuCheckReducer, {
    status: 'IDLE',
    sku: null,
    conflict: null,
  });

  const watchedSku = useWatch({ control, name: 'sku' });
  const debouncedSku = useDebounce(watchedSku, { wait: 800 });

  useEffect(() => {
    if (watchedSku !== debouncedSku) dispatchSku({ type: 'TYPING' });
  }, [watchedSku, debouncedSku]);

  useEffect(() => {
    const sku = (debouncedSku ?? '').trim();
    if (!sku) {
      dispatchSku({ type: 'RESET' });
      return;
    }
    if (
      isEditMode &&
      existingProduct &&
      sku.toLowerCase() === existingProduct.sku.trim().toLowerCase()
    ) {
      dispatchSku({ type: 'RESET' });
      return;
    }
    dispatchSku({ type: 'START', sku });
  }, [debouncedSku, isEditMode, existingProduct]);

  useEffect(() => {
    if (skuCheck.status !== 'VALIDATING' || !skuCheck.sku) return;
    let cancelled = false;
    const sku = skuCheck.sku;
    (async () => {
      try {
        const data = await productApi.checkSku({
          sku,
          ...(isEditMode && productId ? { excludeId: productId } : {}),
        });
        if (cancelled) return;
        if (data.available) dispatchSku({ type: 'AVAILABLE', sku });
        else dispatchSku({ type: 'TAKEN', sku, conflict: data.conflict ?? null });
      } catch {
        if (!cancelled) dispatchSku({ type: 'RESET' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [skuCheck.status, skuCheck.sku, isEditMode, productId]);

  useEffect(() => {
    if (skuCheck.status === 'TAKEN') {
      setError('sku', { type: 'server', message: 'products:validation.skuTaken' });
    } else if (errors.sku?.type === 'server') {
      clearErrors('sku');
    }
  }, [skuCheck.status, errors.sku?.type, setError, clearErrors]);

  // -- 報關品名 live-mirror --------------------------------------------------
  const watchedDisplayName = useWatch({ control, name: 'displayName' });
  useEffect(() => {
    if (customsEditedRef.current) return;
    const next = watchedDisplayName ?? '';
    if (getValues('customsDescription') !== next) {
      setValue('customsDescription', next, { shouldDirty: false });
    }
  }, [watchedDisplayName, getValues, setValue]);

  // -- Live CBM -------------------------------------------------------------
  const [lengthMm, widthMm, heightMm] = useWatch({
    control,
    name: ['lengthMm', 'widthMm', 'heightMm'],
  });
  const cbm = useMemo(() => {
    if (!lengthMm || !widthMm || !heightMm) return null;
    return (lengthMm * widthMm * heightMm) / 1_000_000_000;
  }, [lengthMm, widthMm, heightMm]);

  // -- Live Age Group (C108 【MUST】, S2.1) — SYSTEM-COMPUTED read-only chip
  //    fed by the pure `computeAgeGroup`; the user only enters the year range.
  const [ageStartYear, ageEndYear] = useWatch({
    control,
    name: ['ageStartYear', 'ageEndYear'],
  });
  const ageGroupPreview = computeAgeGroup(ageStartYear, ageEndYear, new Date().getFullYear());

  // -- Collections ----------------------------------------------------------
  const fitmentYears = useWatch({ control, name: 'fitmentYears' }) ?? [];
  const customerPartNumbers = useWatch({ control, name: 'customerPartNumbers' }) ?? [];
  const sparePartNumbers = useWatch({ control, name: 'sparePartNumbers' }) ?? [];
  const vendorLines = useWatch({ control, name: 'vendorLines' }) ?? [];
  /**
   * The SAME capability the detail page gates on, for the SAME reason: no
   * vendor-line write has a graph root while the line read is graph-served, so
   * `addVendor` / `setPreferredVendor` refuse in that posture. The existing
   * lines still RENDER — they are real, they came off the live detail read —
   * but the affordances that would enqueue a doomed write are not offered.
   * Without this the wizard would collect vendor lines, refuse them at save,
   * and report a partial failure for work the user should never have typed.
   */
  const canWriteVendorLine = vendorLineWriteReachesTheRead();
  const images = useWatch({ control, name: 'images' }) ?? [];
  const watchedCategoryId = useWatch({ control, name: 'categoryId' });

  const upsertById = <T,>(rows: T[], row: T, matcher: (candidate: T) => boolean): T[] =>
    rows.some(matcher)
      ? rows.map((candidate) => (matcher(candidate) ? row : candidate))
      : [...rows, row];

  const upsertYearRow = (row: FitmentYearRow) =>
    setValue(
      'fitmentYears',
      upsertById(getValues('fitmentYears'), row, (candidate) => candidate.id === row.id),
      { shouldDirty: true }
    );

  const upsertPnRow = (row: CustomerPartNumberRow) =>
    setValue(
      'customerPartNumbers',
      upsertById(getValues('customerPartNumbers'), row, (candidate) => candidate.id === row.id),
      { shouldDirty: true }
    );

  const upsertSparePnRow = (row: SparePartNumberRow) =>
    setValue(
      'sparePartNumbers',
      upsertById(getValues('sparePartNumbers'), row, (candidate) => candidate.id === row.id),
      { shouldDirty: true }
    );

  const upsertImageRow = (row: ImageSlotRow) =>
    setValue(
      'images',
      upsertById(getValues('images'), row, (candidate) => candidate.slot === row.slot),
      { shouldDirty: true }
    );

  const removeImageRow = (slot: number) =>
    setValue(
      'images',
      getValues('images')
        .filter((candidate) => candidate.slot !== slot)
        .map((candidate, index) => ({ ...candidate, slot: index + 1 })),
      { shouldDirty: true }
    );

  // -- V3-S3 drag & drop read pipeline (demo-honest: data URI, no storage) --
  // Every enqueued file SETTLES its tile — validate → reading → loaded row,
  // or → failed tile (tooLarge / notImage / readFailed). Retry on a failed
  // tile re-opens the picker to swap in a replacement file.
  const [pendingReads, setPendingReads] = useState<
    { id: string; fileName: string; status: 'reading' | 'failed'; errorKey?: PendingImageTile['errorKey'] }[]
  >([]);
  const pendingIdRef = useRef(0);
  // Mirrors pendingReads.length synchronously: N beforeUpload calls from ONE
  // multi-file drop all run before React re-renders, so the state value is
  // stale for every file after the first — the capacity gate must not read it.
  const pendingCountRef = useRef(0);
  const replaceInputRef = useRef<HTMLInputElement | null>(null);
  const replaceTargetRef = useRef<string | null>(null);

  const mutatePendingReads = (
    updater: (
      current: { id: string; fileName: string; status: 'reading' | 'failed'; errorKey?: PendingImageTile['errorKey'] }[]
    ) => { id: string; fileName: string; status: 'reading' | 'failed'; errorKey?: PendingImageTile['errorKey'] }[]
  ) =>
    setPendingReads((current) => {
      const next = updater(current);
      pendingCountRef.current = next.length;
      return next;
    });

  const settlePendingRead = (
    id: string,
    patch: { status: 'failed'; errorKey: PendingImageTile['errorKey'] } | null
  ) =>
    mutatePendingReads((current) =>
      patch === null
        ? current.filter((tile) => tile.id !== id)
        : current.map((tile) => (tile.id === id ? { ...tile, ...patch } : tile))
    );

  const enqueueImageRead = (file: File) => {
    const currentCount = getValues('images').length + pendingCountRef.current;
    if (currentCount >= MAX_PRODUCT_IMAGES) {
      message.warning(t('products:validation.imagesMax'));
      return;
    }
    const id = `read-${(pendingIdRef.current += 1)}`;
    const rejection = validateImageFile(file);
    if (rejection) {
      mutatePendingReads((current) => [
        ...current,
        { id, fileName: file.name, status: 'failed', errorKey: rejection },
      ]);
      return;
    }
    mutatePendingReads((current) => [...current, { id, fileName: file.name, status: 'reading' }]);
    readAndDownscaleImage(file)
      .then((dataUri) => {
        const rows = getValues('images');
        if (rows.length >= MAX_PRODUCT_IMAGES) {
          // Capacity raced away (e.g. a URL row added mid-read): the read must
          // still land somewhere visible — a failed tile, never a silent drop.
          settlePendingRead(id, { status: 'failed', errorKey: 'maxReached' });
          return;
        }
        settlePendingRead(id, null);
        upsertImageRow({ slot: rows.length + 1, url: dataUri, caption: file.name });
      })
      .catch(() => settlePendingRead(id, { status: 'failed', errorKey: 'readFailed' }));
  };

  const retryPendingRead = (id: string) => {
    replaceTargetRef.current = id;
    replaceInputRef.current?.click();
  };

  const handleReplacePicked = (files: FileList | null) => {
    const target = replaceTargetRef.current;
    replaceTargetRef.current = null;
    const file = files?.[0];
    if (!file) return;
    if (target) settlePendingRead(target, null);
    enqueueImageRead(file);
  };

  const pendingImageTiles: PendingImageTile[] = pendingReads.map((tile) => ({
    ...tile,
    onRetry: tile.status === 'failed' ? () => retryPendingRead(tile.id) : undefined,
    onRemove: tile.status === 'failed' ? () => settlePendingRead(tile.id, null) : undefined,
  }));

  const upsertVendorLine = (row: VendorLineRow) => {
    let lines = getValues('vendorLines');
    const exists = lines.some((line) => line.id === row.id);
    lines = exists ? lines.map((line) => (line.id === row.id ? row : line)) : [...lines, row];
    if (row.isPrimary) {
      lines = lines.map((line) => ({ ...line, isPrimary: line.id === row.id }));
    } else if (lines.length > 0 && !lines.some((line) => line.isPrimary)) {
      lines = lines.map((line, index) => ({ ...line, isPrimary: index === 0 }));
    }
    setValue('vendorLines', lines, { shouldDirty: true });
  };

  const removeVendorLine = (id: string) => {
    let lines = getValues('vendorLines').filter((line) => line.id !== id);
    if (lines.length > 0 && !lines.some((line) => line.isPrimary)) {
      lines = lines.map((line, index) => ({ ...line, isPrimary: index === 0 }));
    }
    setValue('vendorLines', lines, { shouldDirty: true });
  };

  const setPrimaryVendorLine = (id: string) => {
    setValue(
      'vendorLines',
      getValues('vendorLines').map((line) => ({ ...line, isPrimary: line.id === id })),
      { shouldDirty: true }
    );
  };

  // -- S7.4 OEM dup-check (advisory) — the shared `useOemDupCheck` seat -----
  //    (ROW 22 hoist). Quiet, 800ms-debounced, FAILS OPEN on error — never
  //    consulted by validateStep / handleCreate gating; submit stays
  //    unblocked throughout. The hook owns debounce + 2-criteria stand-down
  //    (同產品大類 + 同OEM NUMBER) + edit-mode skip-when-unchanged.
  const watchedOemNo = useWatch({ control, name: 'oemNo' });
  const watchedSubsidiaryId = useWatch({ control, name: 'subsidiaryId' });
  const { oemCheck, settledOemNo } = useOemDupCheck({
    oemNo: watchedOemNo,
    categoryId: watchedCategoryId,
    subsidiaryId: watchedSubsidiaryId,
    savedOemNo: isEditMode && existingProduct ? (existingProduct.oemNo ?? '') : null,
    excludeId: isEditMode && productId ? productId : undefined,
  });

  useEffect(() => {
    const oemNo = (settledOemNo ?? '').trim();
    if (promptedOemRef.current && oemNo !== promptedOemRef.current) {
      // The carried SCN belonged to the previously prompted OEM number.
      setPendingScn(null);
    }
  }, [settledOemNo]);

  // MATCH → open the prompt once per settled OEM number (re-openable from the
  // quiet link under the field — mirrors the SKU viewExisting affordance).
  useEffect(() => {
    if (oemCheck.status !== 'MATCH' || !oemCheck.oemNo) return;
    if (promptedOemRef.current === oemCheck.oemNo) return;
    promptedOemRef.current = oemCheck.oemNo;
    setOemPromptScn(linkableOemMatches(oemCheck.matches)[0]?.scn ?? undefined);
    setOemPromptOpen(true);
  }, [oemCheck.status, oemCheck.oemNo, oemCheck.matches]);

  const oemLinkable = linkableOemMatches(oemCheck.matches);
  const oemHasSameMarket = oemCheck.matches.some((match) => match.sameMarket);
  const oemHasCrossMarket = oemCheck.matches.some((match) => !match.sameMarket);

  const subsidiaryLabel = (subsidiaryId: string): string => {
    const option = SUBSIDIARY_OPTIONS.find((candidate) => candidate.value === subsidiaryId);
    return option ? t(option.labelKey) : subsidiaryId;
  };

  /** 視為不同產品 — close, SCN stays blank (board 1% different-entity path). */
  const handleOemDecline = () => {
    setPendingScn(null);
    setOemPromptOpen(false);
  };

  /** 選擇式關聯 — edit mode opens the S7.3 drawer; create mode carries the SCN. */
  const handleOemLink = () => {
    if (isEditMode) {
      setOemPromptOpen(false);
      setScnDrawerOpen(true);
      return;
    }
    if (!oemPromptScn) return;
    setPendingScn(oemPromptScn);
    setCyanPendingReview(false);
    setOemPromptOpen(false);
  };

  // -- Step navigation (forward-only validation) ----------------------------
  const validateStep = async (step: number): Promise<boolean> => {
    const fields = PRODUCT_WIZARD_STEP_FIELDS[step];
    const valid =
      fields.length === 0 ? true : await trigger(fields as Path<ProductWizardFormData>[]);
    if (step === 0) {
      if (skuCheck.status === 'TAKEN') {
        setError('sku', { type: 'server', message: 'products:validation.skuTaken' });
        return false;
      }
      if (skuCheck.status === 'VALIDATING') {
        message.info(t('products:wizard.skuCheck.checking'));
        return false;
      }
    }
    return valid;
  };

  const handleStepChange = async (nextStep: number) => {
    if (nextStep <= currentStep) {
      setCurrentStep(nextStep);
      return;
    }
    for (let step = currentStep; step < nextStep; step += 1) {
      const valid = await validateStep(step);
      setStepErrors((prev) => ({ ...prev, [step]: !valid }));
      if (!valid) {
        setCurrentStep(step);
        message.error(t('products:messages.completeRequired'));
        return;
      }
    }
    setCurrentStep(nextStep);
  };

  const jumpToSkuError = () => {
    setCurrentStep(0);
    setStepErrors((prev) => ({ ...prev, 0: true }));
    setError('sku', { type: 'server', message: 'products:validation.skuTaken' });
    window.setTimeout(() => setFocus('sku'), 0);
  };

  const isSkuTakenError = (error: unknown): boolean =>
    (error as { response?: { status?: number; data?: { error?: string } } })?.response?.data
      ?.error === 'SKU_TAKEN';

  const validateAllSteps = async (): Promise<boolean> => {
    for (let step = 0; step < PRODUCT_WIZARD_STEP_FIELDS.length; step += 1) {
      const valid = await validateStep(step);
      setStepErrors((prev) => ({ ...prev, [step]: !valid }));
      if (!valid) {
        setCurrentStep(step);
        message.error(t('products:messages.completeRequired'));
        return false;
      }
    }
    return true;
  };

  // -- Create (sequential calls + failedOperations[]) -----------------------
  const handleCreate = async () => {
    if (!(await validateAllSteps())) return;
    setIsSaving(true);
    const failedOperations: string[] = [];
    try {
      const data = getValues();
      const created = await productApi.create({
        ...toCreateProductRequest(data, { currencySystemSet: isEnabled('engines.pricing') }),
        ...alignmentFields(),
      });
      const newId = created.data.id;

      // Primary first so the handler's first-line-primary default agrees
      const orderedLines = [...data.vendorLines].sort(
        (a, b) => Number(b.isPrimary) - Number(a.isPrimary)
      );
      for (const line of orderedLines) {
        try {
          await productApi.addVendor(newId, {
            vendorId: line.vendorId,
            vendorName: line.vendorName,
            unitCost: line.unitCost,
            currency: line.currency,
            orderUnit: line.orderUnit ?? undefined,
            isPreferred: line.isPrimary,
            vendorSku: line.vendorSku?.trim() || undefined,
          });
        } catch {
          failedOperations.push(`${t('products:fields.vendor')}: ${line.vendorName}`);
        }
      }

      // S7.4 — retroactive SCN carry from the OEM dup prompt (選擇式關聯,
      // 系統自動帶入 SCN). SCN is NEVER on the create payload; declining the
      // prompt left pendingScn null ⇒ no PATCH ⇒ SCN stays blank.
      const scnPatch = buildPostCreateScnPatch(pendingScn);
      if (scnPatch) {
        try {
          await productApi.update(newId, scnPatch);
        } catch {
          failedOperations.push(`${t('products:oemDup.linkFailed')}: ${scnPatch.scn}`);
        }
      }

      // G-2169-C-021 (DD!21) — carry a toggled-OFF header Switch into the
      // create sequence. NEITHER request type declares `isActive`: it is off
      // `CreateProductRequest` by the create contract, and off
      // `UpdateProductRequest` by D-3 (2026-08-10), which made
      // POST /products/{id}/set-active the ONLY seat activation is settable
      // from — the PATCH leg could neither carry the boolean nor fail when it
      // dropped it. So this is the same retroactive pattern as the SCN carry
      // above, one endpoint over: the handler's create default is active, so
      // the default-ON path needs no call, and only an explicit Inactive
      // choice reaches the setter right after create.
      if (!data.isActive) {
        try {
          await productApi.setActive(newId, false);
        } catch {
          failedOperations.push(t('products:fields.isActive'));
        }
      }

      invalidateProductCaches(newId); // detail + vendors + all options keys
      reset(getValues()); // drop dirty state so the guard releases navigation

      if (failedOperations.length > 0) {
        notification.warning({
          message: t('products:messages.partialFailure'),
          description: failedOperations.join(' / '),
          placement: 'topRight',
        });
        navigate(`/auth/products/${newId}`);
        return;
      }

      // #2168 (G-2166-K-04) — the create journey's hand-off to 商品搭配規則,
      // NAMED in the success modal rather than collected in the wizard (the
      // in-wizard pairing collection was ruled out). SECONDARY only: the
      // primary action stays exactly as it was (go to the new product), and
      // the custom footer keeps that OK button — `OkBtn` renders the same
      // configured okText/okButtonProps/onOk. Never blocking: dismissing the
      // modal (Esc) still just closes it, as before.
      // Gating: shown ALWAYS. The wizard collects no pairing input at all, so
      // there is NO honest signal to gate on — any "looks like a kit" guess
      // over the name/category would misfire in both directions, and hiding
      // the hand-off from a real kit is the worse failure.
      const successModal = modal.success({
        title: t('products:messages.created'),
        content: t('products:messages.createdWithId', {
          id: created.data.internalProductId,
        }),
        okButtonProps: { style: { height: `${controlHeightTokens.controlHeightXL}px` } },
        onOk: () => navigate(`/auth/products/${newId}`),
        footer: (_originNode, { OkBtn }) => (
          <>
            <Button
              style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
              onClick={() => {
                successModal.destroy();
                navigate(productSectionDeepLink(newId, 'pairingRules'));
              }}
            >
              {t('products:pairing.createHandoff')}
            </Button>
            <OkBtn />
          </>
        ),
      });
    } catch (error) {
      if (isSkuTakenError(error)) {
        jumpToSkuError();
      } else {
        message.error(t('common:messages.createFailed'));
      }
    } finally {
      setIsSaving(false);
    }
  };

  // -- Edit save (cumulative: steps up to the current one) ------------------
  const handleSave = async () => {
    if (!isEditMode || !productId) return;
    for (let step = 0; step <= currentStep && step < PRODUCT_WIZARD_STEP_FIELDS.length; step += 1) {
      const valid = await validateStep(step);
      setStepErrors((prev) => ({ ...prev, [step]: !valid }));
      if (!valid) {
        setCurrentStep(step);
        message.error(t('products:messages.completeRequired'));
        return;
      }
    }
    setIsSaving(true);
    const failedOperations: string[] = [];
    try {
      const data = getValues();
      await productApi.update(productId, {
        ...toUpdateProductRequest(data, { currencySystemSet: isEnabled('engines.pricing') }),
        ...alignmentFields(),
      });

      // New vendor lines post through the relations side path
      for (const line of data.vendorLines.filter((candidate) => !candidate.isExisting)) {
        try {
          await productApi.addVendor(productId, {
            vendorId: line.vendorId,
            vendorName: line.vendorName,
            unitCost: line.unitCost,
            currency: line.currency,
            orderUnit: line.orderUnit ?? undefined,
            isPreferred: line.isPrimary,
            vendorSku: line.vendorSku?.trim() || undefined,
          });
        } catch {
          failedOperations.push(`${t('products:fields.vendor')}: ${line.vendorName}`);
        }
      }

      // Primary moved onto an EXISTING line → exclusive set on the server
      const primaryLine = data.vendorLines.find((line) => line.isPrimary);
      if (primaryLine?.isExisting && primaryLine.vendorId !== serverPrimaryVendorRef.current) {
        try {
          await productApi.setPreferredVendor(productId, { vendorId: primaryLine.vendorId });
        } catch {
          failedOperations.push(`${t('products:setPreferred')}: ${primaryLine.vendorName}`);
        }
      }

      invalidateProductCaches(productId); // detail + vendors + all options keys
      const fresh = await productApi.getById(productId);
      reset(hydrateFromProduct(fresh.data));
      setManufacturer(fresh.data.manufacturer ?? '');
      setWeightUnit(fresh.data.weightUnit ?? 'kg');
      setProductNotes(fresh.data.productNotes ?? []);
      setDamageStockNotes(fresh.data.damageStockNotes ?? '');
      setItemComment(fresh.data.itemComment ?? '');
      setPlanning(fresh.data.planning ?? {});
      serverPrimaryVendorRef.current =
        fresh.data.vendorLines?.find((line) => line.isPrimary)?.vendorId ?? null;

      if (failedOperations.length > 0) {
        notification.warning({
          message: t('products:messages.partialFailure'),
          description: failedOperations.join(' / '),
          placement: 'topRight',
        });
      } else {
        message.success(t('products:messages.updated'));
      }
    } catch (error) {
      if (isSkuTakenError(error)) {
        jumpToSkuError();
      } else {
        message.error(t('common:messages.updateFailed'));
      }
    } finally {
      setIsSaving(false);
    }
  };

  // -- Readiness preview (Review step) --------------------------------------
  const watchedAll = useWatch({ control });
  const readinessPreview = useMemo(() => {
    void watchedAll;
    const preview = toReadinessPreview(getValues());
    return computeProductReadiness(preview);
  }, [watchedAll, getValues]);

  const meterItems = useMemo<ReadinessMeterItem[]>(() => {
    const values = getValues();
    const createFields = [
      values.sku,
      values.displayName,
      values.subsidiaryId,
      values.categoryId,
      values.unitsTypeId,
      values.stockUnit,
      values.purchaseUnit,
      values.saleUnit,
    ];
    const createFilled = createFields.filter((value) => Boolean(value && value.trim())).length;
    const descriptions = [
      values.salesDescription,
      values.purchaseDescription,
      values.customsDescription,
    ];
    const descriptionsFilled = descriptions.filter((value) => Boolean(value?.trim())).length;
    return [
      {
        key: 'createRequired',
        label: t('products:wizard.reviewMeter.groups.createRequired'),
        detail: t('products:wizard.reviewMeter.detail', {
          filled: createFilled,
          total: createFields.length,
        }),
        complete: createFilled === createFields.length,
        onClick: () => setCurrentStep(0),
      },
      {
        key: 'descriptions',
        label: t('products:wizard.reviewMeter.groups.descriptions'),
        detail: t('products:wizard.reviewMeter.detail', {
          filled: descriptionsFilled,
          total: descriptions.length,
        }),
        complete: descriptionsFilled === descriptions.length,
        onClick: () => setCurrentStep(2),
      },
      {
        key: 'pricingRef',
        label: t('products:wizard.reviewMeter.groups.pricingRef'),
        complete: values.basePrice != null && values.basePrice > 0,
        onClick: () => setCurrentStep(3),
      },
      {
        key: 'costingRef',
        label: t('products:wizard.reviewMeter.groups.costingRef'),
        complete: values.costingMethod != null,
        onClick: () => setCurrentStep(3),
      },
    ];
  }, [watchedAll, getValues, t]); // eslint-disable-line react-hooks/exhaustive-deps

  const categoryBreadcrumb = useMemo(() => {
    if (!watchedCategoryId) return '';
    const target = taxonomyNodes.find((node) => node.id === watchedCategoryId);
    if (!target) return '';
    const byId = new Map(taxonomyNodes.map((node) => [node.id, node]));
    return target.path
      .split('/')
      .map((id) => {
        const node = byId.get(id);
        return node ? `${node.nameZh} ${node.nameEn}` : id;
      })
      .join(' › ');
  }, [taxonomyNodes, watchedCategoryId]);

  // DD!17–18 (#2172 D-B) — Product Group / Product Sub Group named READ-ONLY
  // slots: the L2/L3 LEVELS of the chosen categoryId path, derived for display
  // beneath the cascader. Never inputs, never registry keys, never form
  // fields — the TaxonomyCascader stays the ONE entry point.
  const categoryPathLevels = useMemo(() => {
    const empty = { productGroup: '', productSubGroup: '' };
    if (!watchedCategoryId) return empty;
    const target = taxonomyNodes.find((node) => node.id === watchedCategoryId);
    if (!target) return empty;
    const byId = new Map(taxonomyNodes.map((node) => [node.id, node]));
    const chain = target.path.split('/').flatMap((id) => {
      const node = byId.get(id);
      return node ? [node] : [];
    });
    const labelAtDepth = (depth: number) => {
      const node = chain.find((item) => item.depth === depth);
      return node ? `${node.nameZh} ${node.nameEn}` : '';
    };
    return { productGroup: labelAtDepth(2), productSubGroup: labelAtDepth(3) };
  }, [taxonomyNodes, watchedCategoryId]);

  // -- Chrome ---------------------------------------------------------------
  const steps: WizardStepItem[] = useMemo(
    () => [
      {
        title: t('products:wizard.steps.identity'),
        description: t('products:wizard.steps.identityHint'),
        icon: <TagsOutlined />,
      },
      {
        title: t('products:wizard.steps.units'),
        description: t('products:wizard.steps.unitsHint'),
        icon: <ColumnHeightOutlined />,
      },
      {
        title: t('products:wizard.steps.descriptions'),
        description: t('products:wizard.steps.descriptionsHint'),
        icon: <FileTextOutlined />,
      },
      {
        // PROPOSED (RULING-3 2026-08-29) — pending 林芊 EN sign-off + Melissa
        // mock walk: flag-picked dual keys; hint unchanged either way.
        title: t(
          pricingOn ? 'products:wizard.steps.pricingCost' : 'products:wizard.steps.commercial'
        ),
        description: t('products:wizard.steps.commercialHint'),
        icon: <DollarOutlined />,
      },
      {
        title: t('products:wizard.steps.review'),
        description: t('products:wizard.steps.reviewHint'),
        icon: <CheckCircleOutlined />,
      },
    ],
    [t, pricingOn]
  );

  const shellTexts: WizardShellTexts = useMemo(
    () => ({
      exit: t('products:wizard.actions.exit'),
      previous: t('products:wizard.actions.previous'),
      next: t('products:wizard.actions.next'),
      save: t('products:wizard.actions.save'),
      submit: t('products:wizard.actions.submit'),
      status: {
        completed: t('products:wizard.status.completed'),
        inProgress: t('products:wizard.status.inProgress'),
        pending: t('products:wizard.status.pending'),
        hasErrors: t('products:wizard.status.hasErrors'),
      },
      stepLabel: (stepNumber: number) => t('products:wizard.stepLabel', { number: stepNumber }),
      progressLabel: (current: number, total: number) =>
        t('products:wizard.progressLabel', { current, total }),
    }),
    [t]
  );

  // -- Shared render bits ---------------------------------------------------
  const requiredMark = <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>;

  const readOnlyBox = (value: ReactNode) => (
    <div
      style={{
        minHeight: `${controlHeightTokens.controlHeightXL}px`,
        borderRadius: `${radiusTokens.card}px`,
        border: `1px solid ${token.colorBorder}`,
        backgroundColor: token.colorFillQuaternary,
        display: 'flex',
        alignItems: 'center',
        padding: `0 ${token.paddingSM}px`,
        color: token.colorTextSecondary,
      }}
    >
      {value}
    </div>
  );

  const rowActionColumn = <T extends { id?: string }>(
    rowName: (row: T) => string,
    onEdit: ((row: T) => void) | null,
    onDelete: ((row: T) => void) | null,
    extraItems?: (row: T) => { key: string; label: ReactNode; onClick: () => void }[]
  ) => ({
    title: t('products:fields.action'),
    key: 'action',
    width: 80,
    fixed: 'right' as const,
    render: (_: unknown, row: T) => {
      const items = [
        ...(extraItems ? extraItems(row) : []),
        ...(onEdit
          ? [{ key: 'edit', label: t('common:actions.edit'), onClick: () => onEdit(row) }]
          : []),
        ...(onDelete
          ? [
              {
                key: 'delete',
                label: t('common:actions.delete'),
                danger: true,
                onClick: () => onDelete(row),
              },
            ]
          : []),
      ];
      if (items.length === 0) return null;
      return (
        <Dropdown
          menu={{
            items: items.map((item) => ({
              key: item.key,
              label: item.label,
              danger: (item as { danger?: boolean }).danger,
              onClick: item.onClick,
            })),
          }}
          trigger={['click']}
        >
          <Button
            type="text"
            aria-label={t('products:actionsFor', { name: rowName(row) })}
            icon={<MoreOutlined />}
          />
        </Dropdown>
      );
    },
  });

  // -- Step ① 識別與分類 ----------------------------------------------------
  const skuSuffix =
    skuCheck.status === 'VALIDATING' ? (
      <LoadingOutlined style={{ color: token.colorPrimary }} />
    ) : skuCheck.status === 'AVAILABLE' ? (
      <CheckCircleFilled style={{ color: token.colorSuccess }} />
    ) : skuCheck.status === 'TAKEN' ? (
      <CloseCircleFilled style={{ color: token.colorError }} />
    ) : (
      <span />
    );

  const renderIdentityStep = () => (
    <>
      <FormSection title={t('products:sections.coreIdentity')}>
        {isEditMode ? (
          <div style={{ marginBottom: '16px' }}>
            <Text style={styles.fieldLabel}>{t('products:fields.internalProductId')}</Text>
            {readOnlyBox(
              <Text strong style={{ color: token.colorText }}>
                {existingProduct?.internalProductId ?? '-'}
              </Text>
            )}
          </div>
        ) : (
          <Alert
            type="info"
            showIcon
            message={t('products:wizard.internalIdBanner')}
            style={{ marginBottom: '16px' }}
          />
        )}
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Controller
              name="sku"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <div>
                  <Text style={styles.fieldLabel}>
                    {requiredMark}
                    {t('products:fields.sku')}
                  </Text>
                  <Input
                    {...field}
                    size="large"
                    suffix={skuSuffix}
                    status={error ? 'error' : undefined}
                    style={error ? styles.formInputError : styles.formInput}
                  />
                  {error ? (
                    <Text style={styles.errorText}>{t(error.message ?? '')}</Text>
                  ) : skuCheck.status === 'VALIDATING' ? (
                    <Text style={styles.helpText}>{t('products:wizard.skuCheck.checking')}</Text>
                  ) : skuCheck.status === 'AVAILABLE' ? (
                    <Text style={{ ...styles.helpText, color: token.colorSuccess }}>
                      {t('products:wizard.skuCheck.available')}
                    </Text>
                  ) : null}
                  {skuCheck.status === 'TAKEN' && skuCheck.conflict && (
                    <Button
                      type="link"
                      size="small"
                      style={{ paddingLeft: 0 }}
                      onClick={() => setConflictModalOpen(true)}
                    >
                      {t('products:wizard.skuCheck.viewExisting')}
                    </Button>
                  )}
                </div>
              )}
            />
          </Col>
          <Col span={12}>
            <WizardTextField
              name="displayName"
              control={control}
              label={t('products:fields.displayName')}
              required
            />
          </Col>
        </Row>
      </FormSection>

      <FormSection title={t('products:sections.classification')}>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            {/*
              CREATE-ONLY (D-5, 2026-08-10). ProductUpdateInput declares no
              subsidiary field and the server patches no subsidiary column, so
              in edit mode this Select offered a write that could not happen —
              and because the facade dropped the field before the wire, the save
              answered 200 and the row never moved. Read-only in edit mode, the
              same treatment the internal product id already gets one section
              up: the field is still shown, it just stops claiming to be
              settable. See productWriteFieldParity.test.ts for the guard.
            */}
            {isEditMode ? (
              <div>
                <Text style={styles.fieldLabel}>{t('products:fields.subsidiary')}</Text>
                {readOnlyBox(
                  <Text strong style={{ color: token.colorText }}>
                    {subsidiaryLabel(getValues('subsidiaryId'))}
                  </Text>
                )}
              </div>
            ) : (
              <WizardSelectField
                name="subsidiaryId"
                control={control}
                label={t('products:fields.subsidiary')}
                required
                options={SUBSIDIARY_OPTIONS.map((option) => ({
                  label: t(option.labelKey),
                  value: option.value,
                }))}
                onValueChange={() => trigger('subsidiaryId')}
              />
            )}
          </Col>
          <Col span={12}>
            <Controller
              name="includeChildren"
              control={control}
              render={({ field }) => (
                <div>
                  <Text style={styles.fieldLabel}>{t('products:fields.includeChildren')}</Text>
                  <div
                    style={{
                      minHeight: `${controlHeightTokens.controlHeightXL}px`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <Switch checked={field.value} onChange={(checked) => field.onChange(checked)} />
                    <Text style={{ color: token.colorTextSecondary }}>
                      {t('products:wizard.includeChildrenHelp')}
                    </Text>
                  </div>
                </div>
              )}
            />
          </Col>
        </Row>
        <div style={{ marginTop: '16px' }}>
          <Text style={styles.fieldLabel}>
            {requiredMark}
            {t('products:fields.category')}
          </Text>
          <TaxonomyCascader
            value={watchedCategoryId || null}
            status={errors.categoryId ? 'error' : undefined}
            onChange={(categoryId) => {
              setValue('categoryId', categoryId ?? '', { shouldDirty: true });
              trigger('categoryId');
            }}
            texts={{
              placeholder: (label) => t('products:wizard.cascader.selectLevel', { label }),
              createLabel: (label) => t('products:wizard.cascader.createInLevel', { label }),
              nonLeafWarning: t('products:wizard.cascader.nonLeafWarning'),
              breadcrumbLabel: t('products:wizard.cascader.breadcrumbLabel'),
            }}
            onRequestCreate={(parentId) => {
              setCreateNodeParentId(parentId);
              setCreateNodeOpen(true);
            }}
          />
          {errors.categoryId && (
            <Text style={styles.errorText}>{t(errors.categoryId.message ?? '')}</Text>
          )}
          {/* DD!17–18 (#2172 D-B) — named read-only derived slots showing the
              Product Group (L2) / Product Sub Group (L3) levels of the chosen
              path; dash until a path reaches that level. Display only. */}
          <Row gutter={[16, 16]} style={{ marginTop: '12px' }}>
            <Col span={12}>
              <Text style={styles.fieldLabel}>{t('products:wizard.pathSlots.productGroup')}</Text>
              <Text>{renderDash(categoryPathLevels.productGroup || undefined)}</Text>
            </Col>
            <Col span={12}>
              <Text style={styles.fieldLabel}>
                {t('products:wizard.pathSlots.productSubGroup')}
              </Text>
              <Text>{renderDash(categoryPathLevels.productSubGroup || undefined)}</Text>
            </Col>
          </Row>
        </div>
      </FormSection>
    </>
  );

  // -- Step ② 單位與規格 ----------------------------------------------------
  const unitOptions = UNIT_VALUES.map((unit) => ({ label: unit, value: unit }));

  const renderUnitsStep = () => (
    <>
      <FormSection title={t('products:sections.units')}>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            {/*
              CREATE-ONLY (D-4, 2026-08-10) — same contract read as the
              subsidiary field above: the update input declares no units-type
              field and the server maps no units-type column. This one was the
              LOUD half: the field is always populated, so every edit save put
              an undeclared field on the wire and the graph refused the whole
              mutation with a 400, taking the user's real edits down with it.
            */}
            {isEditMode ? (
              <div>
                <Text style={styles.fieldLabel}>{t('products:fields.unitsType')}</Text>
                {readOnlyBox(
                  <Text strong style={{ color: token.colorText }}>
                    {getValues('unitsTypeId') === 'UT-QTY'
                      ? t('products:wizard.unitsTypes.quantity')
                      : getValues('unitsTypeId')}
                  </Text>
                )}
              </div>
            ) : (
              <WizardSelectField
                name="unitsTypeId"
                control={control}
                label={t('products:fields.unitsType')}
                required
                options={[{ label: t('products:wizard.unitsTypes.quantity'), value: 'UT-QTY' }]}
                onValueChange={() => trigger('unitsTypeId')}
              />
            )}
          </Col>
          <Col span={12}>
            <WizardSelectField
              name="stockUnit"
              control={control}
              label={t('products:fields.stockUnit')}
              required
              options={unitOptions}
              onValueChange={() => trigger('stockUnit')}
            />
          </Col>
          <Col span={12}>
            <WizardSelectField
              name="purchaseUnit"
              control={control}
              label={t('products:fields.purchaseUnit')}
              required
              options={unitOptions}
              onValueChange={() => trigger('purchaseUnit')}
            />
          </Col>
          <Col span={12}>
            <WizardSelectField
              name="saleUnit"
              control={control}
              label={t('products:fields.saleUnit')}
              required
              options={unitOptions}
              onValueChange={() => trigger('saleUnit')}
            />
          </Col>
        </Row>
      </FormSection>

      <FormSection title={t('products:wizard.sections.physical')}>
        <Row gutter={[16, 16]}>
          <Col span={8}>
            <WizardNumberField
              name="lengthMm"
              control={control}
              label={t('products:fields.lengthMm')}
              min={0}
            />
          </Col>
          <Col span={8}>
            <WizardNumberField
              name="widthMm"
              control={control}
              label={t('products:fields.widthMm')}
              min={0}
            />
          </Col>
          <Col span={8}>
            <WizardNumberField
              name="heightMm"
              control={control}
              label={t('products:fields.heightMm')}
              min={0}
            />
          </Col>
          <Col span={8}>
            {/* Weight value + Item Weight Unit qualifier (0708-J Must; field name weightKg kept) */}
            <Controller
              name="weightKg"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <div>
                  <Text style={styles.fieldLabel}>{t('products:fields.weight')}</Text>
                  <Space.Compact style={{ width: '100%' }}>
                    <InputNumber
                      size="large"
                      min={0}
                      value={(field.value as number | null | undefined) ?? null}
                      onChange={(value) => field.onChange(value ?? null)}
                      onBlur={field.onBlur}
                      status={error ? 'error' : undefined}
                      style={{ flex: 1 }}
                    />
                    <Select
                      size="large"
                      aria-label={t('products:fields.weightUnit')}
                      value={weightUnit}
                      options={PRODUCT_WEIGHT_UNITS.map((unit) => ({
                        label: t(`products:weightUnits.${unit}`),
                        value: unit,
                      }))}
                      onChange={(value) => setWeightUnit(value)}
                      style={{ width: 84 }}
                    />
                  </Space.Compact>
                  {error && <Text style={styles.errorText}>{t(error.message ?? '')}</Text>}
                </div>
              )}
            />
          </Col>
          <Col span={16}>
            <Text style={styles.fieldLabel}>{t('products:fields.cbm')}</Text>
            {readOnlyBox(
              cbm != null ? (
                <Text strong style={{ color: token.colorText }}>
                  {`${cbm.toFixed(4)} m³ · ${(cbm * 35.3147).toFixed(2)} ft³`}
                </Text>
              ) : (
                '-'
              )
            )}
            <Text style={styles.helpText}>{t('products:wizard.cbmAuto')}</Text>
          </Col>
        </Row>
      </FormSection>

      <FormSection title={t('products:wizard.sections.fitment')}>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <WizardSelectField
              name="fitmentMakes"
              control={control}
              label={t('products:fields.fitmentMakes')}
              mode="tags"
              showSearch
              options={MAKE_SUGGESTIONS.map((make) => ({ label: make, value: make }))}
            />
          </Col>
          <Col span={12}>
            <WizardSelectField
              name="fitmentModels"
              control={control}
              label={t('products:fields.fitmentModels')}
              mode="tags"
              showSearch
              options={MODEL_SUGGESTIONS.map((model) => ({ label: model, value: model }))}
            />
          </Col>
          <Col span={12}>
            {/* DD!31 (S2.1) — product-level Car Details variant, distinct from
                the per-year-range trim note (Q-T3). */}
            <WizardTextField
              name="variant"
              control={control}
              label={t('products:fields.variant')}
            />
          </Col>
          <Col span={12}>
            {/* DD!32 (S2.1) — extract year 拆解年份. */}
            <WizardNumberField
              name="extractYear"
              control={control}
              label={t('products:fields.extractYear')}
              min={1950}
              max={2100}
              precision={0}
            />
          </Col>
        </Row>
        <div style={{ marginTop: '16px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px',
            }}
          >
            <Text style={styles.fieldLabel}>{t('products:fields.fitmentYears')}</Text>
            <Button
              icon={<PlusOutlined />}
              style={styles.addButton}
              onClick={() => setYearModal({ open: true, row: null })}
            >
              {t('products:wizard.addYear')}
            </Button>
          </div>
          <TableContainer>
            <Table<FitmentYearRow>
              dataSource={fitmentYears as FitmentYearRow[]}
              rowKey="id"
              size="small"
              pagination={false}
              columns={[
                {
                  title: t('products:fields.fitmentYears'),
                  key: 'range',
                  render: (_, row) => `${row.yearFrom}${row.yearTo ? ` – ${row.yearTo}` : ' –'}`,
                },
                {
                  title: t('products:fields.variant'),
                  dataIndex: 'variant',
                  key: 'variant',
                  render: (value: string | undefined) => value || '-',
                },
                rowActionColumn<FitmentYearRow>(
                  (row) => `${row.yearFrom}${row.yearTo ? ` – ${row.yearTo}` : ' –'}`,
                  (row) => setYearModal({ open: true, row }),
                  (row) =>
                    setValue(
                      'fitmentYears',
                      getValues('fitmentYears').filter((candidate) => candidate.id !== row.id),
                      { shouldDirty: true }
                    )
                ),
              ]}
            />
          </TableContainer>
        </div>
      </FormSection>

      <FormSection title={t('products:wizard.sections.manufacturing')}>
        <Row gutter={[16, 16]}>
          <Col span={8}>
            {/* 製造商 (0708-J Must) — distinct from MPN / vendor sourcing; local state */}
            <div>
              <Text style={styles.fieldLabel}>{t('products:fields.manufacturer')}</Text>
              <Input
                size="large"
                value={manufacturer}
                onChange={(event) => setManufacturer(event.target.value)}
                style={styles.formInput}
              />
            </div>
          </Col>
          <Col span={8}>
            <WizardTextField name="mpn" control={control} label={t('products:fields.mpn')} />
          </Col>
          <Col span={8}>
            <WizardTextField
              name="countryOfManufacture"
              control={control}
              label={t('products:fields.countryOfManufacture')}
            />
          </Col>
          <Col span={8}>
            {/* DD!69 FA Sub Category (S2.1) — manufacturing sub-classification. */}
            <WizardSelectField
              name="faSubCategory"
              control={control}
              label={t('products:fields.faSubCategory')}
              allowClear
              showSearch
              options={FA_SUB_CATEGORY_VALUES.map((value) => ({ label: value, value }))}
            />
          </Col>
        </Row>
      </FormSection>

      <FormSection title={t('products:wizard.sections.attributes')}>
        {/* C108 【MUST】 (S2.1) — the user enters the vehicle start/end year;
            the Age Group 車齡組別 is SYSTEM-COMPUTED (pure computeAgeGroup) and
            renders as a read-only chip — no direct entry anywhere. */}
        <Row gutter={[16, 16]}>
          <Col span={6}>
            <WizardNumberField
              name="ageStartYear"
              control={control}
              label={t('products:fields.ageStartYear')}
              min={1950}
              max={2100}
              precision={0}
            />
          </Col>
          <Col span={6}>
            <WizardNumberField
              name="ageEndYear"
              control={control}
              label={t('products:fields.ageEndYear')}
              min={1950}
              max={2100}
              precision={0}
            />
          </Col>
          <Col span={12}>
            <Text style={styles.fieldLabel}>{t('products:fields.ageGroup')}</Text>
            {readOnlyBox(
              ageGroupPreview ? (
                <Tag style={{ margin: 0 }}>{t(`products:ageGroupBands.${ageGroupPreview}`)}</Tag>
              ) : (
                '-'
              )
            )}
            <Text style={styles.helpText}>{t('products:wizard.ageGroupAuto')}</Text>
          </Col>
        </Row>
      </FormSection>
    </>
  );

  // -- Step ③ 描述與識別碼 --------------------------------------------------
  const renderDescriptionsStep = () => (
    <>
      <FormSection title={t('products:sections.businessDescriptions')}>
        <WizardTextAreaField
          name="salesDescription"
          control={control}
          label={t('products:fields.salesDescription')}
          help={t('products:wizard.descriptionsHelp.sales')}
        />
        <div style={{ marginTop: '16px' }}>
          <WizardTextAreaField
            name="purchaseDescription"
            control={control}
            label={t('products:fields.purchaseDescription')}
            help={t('products:wizard.descriptionsHelp.purchase')}
          />
        </div>
        <div style={{ marginTop: '16px' }}>
          <WizardTextAreaField
            name="customsDescription"
            control={control}
            label={t('products:fields.customsDescription')}
            help={t('products:wizard.descriptionsHelp.customs')}
            onValueChange={(value) => {
              customsEditedRef.current =
                value.trim().length > 0 && value !== getValues('displayName');
            }}
          />
        </div>
      </FormSection>

      <FormSection title={t('products:wizard.sections.notes')}>
        {/* 商品備註 (US-183) — minimal list editor; the operation-triggered
            pop-up behaviour is explicitly out of scope (schema + CRUD only) */}
        <Text style={styles.fieldLabel}>{t('products:fields.productNotes')}</Text>
        <Text style={{ ...styles.helpText, display: 'block', marginBottom: '8px' }}>
          {t('products:notes.help')}
        </Text>
        <Row gutter={[16, 16]} style={{ marginBottom: '8px' }}>
          <Col span={6}>
            <Select
              size="large"
              aria-label={t('products:notes.audience')}
              value={noteDraft.audience}
              options={NOTE_AUDIENCES.map((audience) => ({
                label: t(`products:notes.audiences.${audience}`),
                value: audience,
              }))}
              onChange={(audience) => setNoteDraft((draft) => ({ ...draft, audience }))}
              style={styles.formInput}
            />
          </Col>
          <Col span={14}>
            <Input
              size="large"
              value={noteDraft.text}
              placeholder={t('products:notes.textPlaceholder')}
              onChange={(event) =>
                setNoteDraft((draft) => ({ ...draft, text: event.target.value }))
              }
              onPressEnter={addProductNote}
              style={styles.formInput}
            />
          </Col>
          <Col span={4}>
            {noteDraft.text.trim().length > 0 && (
              <Button
                icon={<PlusOutlined />}
                style={{ ...styles.addButton, width: '100%' }}
                onClick={addProductNote}
              >
                {t('products:notes.add')}
              </Button>
            )}
          </Col>
        </Row>
        <TableContainer>
          <Table<ProductNote>
            dataSource={productNotes}
            rowKey="id"
            size="small"
            pagination={false}
            locale={{ emptyText: t('products:notes.empty') }}
            columns={[
              {
                title: t('products:notes.audience'),
                dataIndex: 'audience',
                key: 'audience',
                width: 140,
                render: (audience: ProductNoteAudience) => (
                  <Tag color="purple">{t(`products:notes.audiences.${audience}`)}</Tag>
                ),
              },
              {
                title: t('products:notes.text'),
                dataIndex: 'text',
                key: 'text',
              },
              rowActionColumn<ProductNote>(
                (row) => row.text,
                null,
                (row) => removeProductNote(row.id)
              ),
            ]}
          />
        </TableContainer>

        <div style={{ marginTop: '16px' }}>
          <Text style={styles.fieldLabel}>{t('products:fields.damageStockNotes')}</Text>
          <Input.TextArea
            rows={3}
            value={damageStockNotes}
            onChange={(event) => setDamageStockNotes(event.target.value)}
            style={{ ...styles.formInput, height: 'auto' }}
          />
        </div>
        <div style={{ marginTop: '16px' }}>
          <Text style={styles.fieldLabel}>{t('products:fields.itemComment')}</Text>
          <Input.TextArea
            rows={3}
            value={itemComment}
            onChange={(event) => setItemComment(event.target.value)}
            style={{ ...styles.formInput, height: 'auto' }}
          />
        </div>
      </FormSection>

      <FormSection title={t('products:sections.externalIdentifiers')}>
        <Row gutter={[16, 16]}>
          <Col span={8}>
            <div>
              <Text style={styles.fieldLabel}>{t('products:fields.netSuiteItemId')}</Text>
              {readOnlyBox(existingProduct?.netSuiteItemId ?? t('products:notSynced'))}
              <Text style={styles.helpText}>{t('products:netSuiteHelp')}</Text>
            </div>
          </Col>
          <Col span={8}>
            <Controller
              name="oemNo"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <div>
                  <Text style={styles.fieldLabel}>{t('products:fields.oemNo')}</Text>
                  <Input
                    {...field}
                    value={(field.value as string | undefined) ?? ''}
                    size="large"
                    // Quiet in-flight indicator — advisory check: no blocking
                    // spinner, submit never disabled while checking.
                    suffix={
                      oemCheck.status === 'VALIDATING' ? (
                        <LoadingOutlined style={{ color: token.colorTextTertiary }} />
                      ) : (
                        <span />
                      )
                    }
                    status={error ? 'error' : undefined}
                    style={error ? styles.formInputError : styles.formInput}
                  />
                  {error && <Text style={styles.errorText}>{t(error.message ?? '')}</Text>}
                  {pendingScn ? (
                    <div
                      className="tw-flex tw-items-center tw-gap-2"
                      style={{ marginTop: '4px', flexWrap: 'wrap' }}
                    >
                      <PurpleTag>{pendingScn}</PurpleTag>
                      <Text style={styles.helpText}>{t('products:oemDup.willLink')}</Text>
                      <Button
                        type="link"
                        size="small"
                        style={{ paddingLeft: 0 }}
                        onClick={() => setPendingScn(null)}
                      >
                        {t('products:oemDup.cancelCarry')}
                      </Button>
                    </div>
                  ) : cyanPendingReview ? (
                    <div style={{ marginTop: '4px' }}>
                      <Tag color="cyan" closable onClose={() => setCyanPendingReview(false)}>
                        {t('products:oemDup.pendingReviewBadge')}
                      </Tag>
                    </div>
                  ) : oemCheck.status === 'MATCH' ? (
                    <Button
                      type="link"
                      size="small"
                      style={{ paddingLeft: 0 }}
                      onClick={() => setOemPromptOpen(true)}
                    >
                      {t('products:oemDup.viewMatches')}
                    </Button>
                  ) : oemCheck.status === 'CLEAR' ? (
                    // Cyan board case (OEM不同但確定共用) — quiet text
                    // affordance; recording is a pending-review badge ONLY
                    // (approval workflow descoped, Q-O3).
                    <Button
                      type="link"
                      size="small"
                      style={{ paddingLeft: 0, color: token.colorTextSecondary }}
                      onClick={() => setCyanPendingReview(true)}
                    >
                      {t('products:oemDup.cyanAffordance')}
                    </Button>
                  ) : null}
                </div>
              )}
            />
          </Col>
          <Col span={8}>
            <WizardTextField
              name="upcCode"
              control={control}
              label={t('products:fields.upcCode')}
            />
          </Col>
        </Row>
      </FormSection>

      <FormSection title={t('products:wizard.sections.customerPns')}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
          <Button
            icon={<PlusOutlined />}
            style={styles.addButton}
            onClick={() => setPnModal({ open: true, row: null })}
          >
            {t('products:wizard.addPn')}
          </Button>
        </div>
        <TableContainer>
          <Table<CustomerPartNumberRow>
            dataSource={customerPartNumbers as CustomerPartNumberRow[]}
            rowKey="id"
            size="small"
            pagination={false}
            columns={[
              {
                title: t('products:fields.customer'),
                dataIndex: 'customerName',
                key: 'customerName',
              },
              {
                title: t('products:fields.partNumber'),
                dataIndex: 'partNumber',
                key: 'partNumber',
              },
              {
                title: t('products:fields.description'),
                dataIndex: 'description',
                key: 'description',
                render: (value: string | undefined) => value || '-',
              },
              rowActionColumn<CustomerPartNumberRow>(
                (row) => row.partNumber,
                (row) => setPnModal({ open: true, row }),
                (row) =>
                  setValue(
                    'customerPartNumbers',
                    getValues('customerPartNumbers').filter((candidate) => candidate.id !== row.id),
                    { shouldDirty: true }
                  )
              ),
            ]}
          />
        </TableContainer>
      </FormSection>

      {/* 備件料號 (2026-08-27 §5) — party-less sibling of 客戶料號. */}
      <FormSection title={t('products:wizard.sections.sparePns')}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
          <Button
            icon={<PlusOutlined />}
            style={styles.addButton}
            onClick={() => setSparePnModal({ open: true, row: null })}
          >
            {t('products:wizard.addSparePn')}
          </Button>
        </div>
        <TableContainer>
          <Table<SparePartNumberRow>
            dataSource={sparePartNumbers as SparePartNumberRow[]}
            rowKey="id"
            size="small"
            pagination={false}
            columns={[
              {
                title: t('products:fields.partNumber'),
                dataIndex: 'partNumber',
                key: 'partNumber',
              },
              {
                title: t('products:fields.description'),
                dataIndex: 'description',
                key: 'description',
                render: (value: string | undefined) => value || '-',
              },
              rowActionColumn<SparePartNumberRow>(
                (row) => row.partNumber,
                (row) => setSparePnModal({ open: true, row }),
                (row) =>
                  setValue(
                    'sparePartNumbers',
                    getValues('sparePartNumbers').filter((candidate) => candidate.id !== row.id),
                    { shouldDirty: true }
                  )
              ),
            ]}
          />
        </TableContainer>
      </FormSection>

      <FormSection title={t('products:wizard.sections.vendorLines')}>
        {canWriteVendorLine ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
            <Button
              icon={<PlusOutlined />}
              style={styles.addButton}
              onClick={() => setVendorModal({ open: true, row: null })}
            >
              {t('products:addVendor')}
            </Button>
          </div>
        ) : (
          <Text type="secondary" style={{ display: 'block', marginBottom: '8px' }}>
            {t('products:vendorLine.editPending')}
          </Text>
        )}
        <TableContainer>
          <Table<VendorLineRow>
            dataSource={vendorLines as VendorLineRow[]}
            rowKey="id"
            size="small"
            pagination={false}
            columns={[
              {
                title: t('products:fields.vendor'),
                key: 'vendorName',
                render: (_, row) => (
                  <span>
                    {row.vendorName}
                    {row.isPrimary && (
                      <Tag color="purple" style={{ marginLeft: 8 }}>
                        {t('products:preferred')}
                      </Tag>
                    )}
                  </span>
                ),
              },
              {
                title: t('products:fields.vendorSku'),
                dataIndex: 'vendorSku',
                key: 'vendorSku',
                render: (value: string | undefined) => value || '-',
              },
              {
                title: t('products:fields.unitCost'),
                key: 'unitCost',
                render: (_, row) => `${row.unitCost} ${row.currency}`,
              },
              {
                // W7-B3 訂購單位 — display-only (no conversion, Q5).
                title: t('products:fields.orderUnit'),
                dataIndex: 'orderUnit',
                key: 'orderUnit',
                render: (value: string | null | undefined) => value || '-',
              },
              {
                title: t('products:fields.leadTimeDays'),
                dataIndex: 'leadTimeDays',
                key: 'leadTimeDays',
                render: (value: number | null | undefined) =>
                  value != null ? t('products:days', { count: value }) : '-',
              },
              // Same gate as the add button: with no reachable write, moving
              // the primary flag or editing a line offers a change that cannot
              // be saved. `rowActionColumn` renders nothing for an empty item
              // list, so the column collapses rather than opening empty.
              rowActionColumn<VendorLineRow>((row) => row.vendorName, null, null, (row) => [
                ...(canWriteVendorLine && !row.isPrimary
                  ? [
                      {
                        key: 'setPrimary',
                        label: t('products:setPreferred'),
                        onClick: () => setPrimaryVendorLine(row.id),
                      },
                    ]
                  : []),
                ...(canWriteVendorLine && !row.isExisting
                  ? [
                      {
                        key: 'edit',
                        label: t('common:actions.edit'),
                        onClick: () => setVendorModal({ open: true, row }),
                      },
                      {
                        key: 'delete',
                        label: t('common:actions.delete'),
                        danger: true,
                        onClick: () => removeVendorLine(row.id),
                      } as { key: string; label: ReactNode; onClick: () => void },
                    ]
                  : []),
              ]),
            ]}
          />
        </TableContainer>
      </FormSection>

      <FormSection title={t('products:wizard.sections.images')}>
        {/* V3-S3 — gallery tiles replace the URL text table; ImageSlotModal
            stays as the URL entry path (add tile / per-tile Edit). */}
        <ProductImageGallery
          images={images as ImageSlotRow[]}
          editable
          maxImages={MAX_PRODUCT_IMAGES}
          onAdd={() => setImageModal({ open: true, row: null })}
          onEdit={(row) => setImageModal({ open: true, row: row as ImageSlotRow })}
          onRemove={removeImageRow}
          pending={pendingImageTiles}
        />
        {/* Drag & drop is DEMO-ONLY (no real storage yet — V3-S6 build-seam
            gate): file → downscaled JPEG data URI → an ordinary image row.
            A real-API build renders the gallery + URL entry alone. */}
        {imageUploadDemoAllowed() &&
          images.length + pendingImageTiles.length < MAX_PRODUCT_IMAGES && (
            <div style={{ marginTop: '16px' }}>
              <Upload.Dragger
                multiple
                accept={ACCEPTED_IMAGE_ACCEPT}
                showUploadList={false}
                beforeUpload={(file) => {
                  enqueueImageRead(file);
                  return false;
                }}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">{t('products:images.dropHint')}</p>
                <p className="ant-upload-hint">{t('products:validation.imagesMax')}</p>
              </Upload.Dragger>
              {/* Hidden picker backing the failed-tile Retry (swap the file). */}
              <input
                ref={replaceInputRef}
                type="file"
                accept={ACCEPTED_IMAGE_ACCEPT}
                style={{ display: 'none' }}
                onChange={(event) => {
                  handleReplacePicked(event.target.files);
                  event.target.value = '';
                }}
              />
            </div>
          )}
      </FormSection>
    </>
  );

  // -- Step ④ 商務與會計 ----------------------------------------------------
  const accountingRules = useMemo(() => getRulesBySection('accounting'), []);

  const renderCommercialStep = () => (
    <>
      {/* PROPOSED (RULING-3 2026-08-29) — pending 林芊 EN sign-off + Melissa
          mock walk: dual keys, legacy value at flag-off. */}
      <FormSection
        title={t(
          pricingOn ? 'products:wizard.sections.pricingCost' : 'products:wizard.sections.commercial'
        )}
      >
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <WizardSelectField
              name="costingMethod"
              control={control}
              label={t('products:fields.costingMethod')}
              allowClear
              options={COSTING_METHOD_VALUES.map((method) => ({
                label: t(`products:wizard.costingMethods.${method}`),
                value: method,
              }))}
            />
          </Col>
          <Col span={12}>
            {currencySystemSet ? (
              /* RULING-2 (2026-08-29) W6 lock display, flag-gated — same
                 recipe as EditCommercialModal: read-only text + lock tag +
                 "!" reason popover; never a disabled Select. The field stays
                 in the schema/form; only the render + request body change. */
              <div>
                <Text style={formStyles.labelStyle}>
                  {t('products:fields.currency')}
                  <Popover
                    trigger={['hover', 'focus']}
                    content={
                      <div style={{ maxWidth: 320 }}>
                        {t('products:detail.currencySystemSet.reason')}
                      </div>
                    }
                  >
                    <span
                      tabIndex={0}
                      role="img"
                      aria-label={t('products:detail.infoAria')}
                      style={{
                        marginInlineStart: 6,
                        color: token.colorTextTertiary,
                        cursor: 'help',
                      }}
                    >
                      <InfoCircleOutlined />
                    </span>
                  </Popover>
                </Text>
                <div
                  tabIndex={0}
                  aria-describedby="wizard-currency-system-set-reason"
                  style={{
                    ...formStyles.inputStyle,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    minHeight: `${controlHeightTokens.controlHeightXL}px`,
                    background: token.colorFillQuaternary,
                    border: `1px dashed ${token.colorBorder}`,
                    borderRadius: token.borderRadiusLG,
                    paddingInline: token.paddingSM,
                  }}
                >
                  <Text>{watchedCurrencyValue ?? 'AUD'}</Text>
                  <Tag icon={<LockOutlined />} style={{ margin: 0 }}>
                    {t('products:detail.currencySystemSet.tag')}
                  </Tag>
                  <span id="wizard-currency-system-set-reason" style={{ display: 'none' }}>
                    {t('products:detail.currencySystemSet.reason')}
                  </span>
                </div>
              </div>
            ) : (
              <WizardSelectField
                name="currency"
                control={control}
                label={t('products:fields.currency')}
                options={CURRENCY_VALUES.map((code) => ({ label: code, value: code }))}
                onValueChange={() => trigger('currency')}
              />
            )}
          </Col>
          <Col span={8}>
            <WizardNumberField
              name="basePrice"
              control={control}
              label={t('products:fields.basePrice')}
              min={0}
            />
          </Col>
          <Col span={8}>
            {/* DD!98 (S2.1) — value + unit qualifier, both schema-backed. */}
            <WizardNumberWithUnitField
              name="itemDefinedCost"
              control={control}
              label={t('products:fields.itemDefinedCost')}
              min={0}
              unitName="itemDefinedCostUnit"
              unitAriaLabel={t('products:fields.itemDefinedCostUnit')}
              unitOptions={unitOptions}
            />
          </Col>
          <Col span={8}>
            <div />
          </Col>
          <Col span={8}>
            {/* DD!100 (S2.1) — value + unit qualifier. */}
            <WizardNumberWithUnitField
              name="minQty"
              control={control}
              label={t('products:fields.minQty')}
              min={0}
              precision={0}
              unitName="minQtyUnit"
              unitAriaLabel={t('products:fields.minQtyUnit')}
              unitOptions={unitOptions}
            />
          </Col>
          <Col span={8}>
            {/* DD!102 (S2.1) — value + unit qualifier. */}
            <WizardNumberWithUnitField
              name="maxQty"
              control={control}
              label={t('products:fields.maxQty')}
              min={0}
              precision={0}
              unitName="maxQtyUnit"
              unitAriaLabel={t('products:fields.maxQtyUnit')}
              unitOptions={unitOptions}
            />
          </Col>
        </Row>
      </FormSection>

      <FormSection title={t('products:wizard.sections.accounting')}>
        <Text style={{ ...styles.helpText, marginBottom: '8px' }}>
          {t('products:wizard.sections.accountingHint')}
        </Text>
        <Collapse
          ghost
          // #2172 D-A — CREATE mode paints the 10 DD!217–226 accounting Musts
          // expanded on FIRST PAINT (SHOW-OPEN; the panel stays collapsible).
          // EDIT mode keeps the collapsed default; the SHOW-on-create suite's
          // render probe can still force-expand either mode.
          defaultActiveKey={!isEditMode || showProbe?.expandAccounting ? ['accounting'] : undefined}
          items={[
            {
              key: 'accounting',
              label: t('products:wizard.sections.accounting'),
              children: (
                <Row gutter={[16, 16]}>
                  {accountingRules.map((rule) => {
                    const label = rule.labelKey ? t(rule.labelKey) : rule.key;
                    if (rule.blocked) {
                      return (
                        <Col span={12} key={rule.key}>
                          <BlockedFieldCallout
                            label={label}
                            statusText={t('products:wizard.blocked.status')}
                            description={t('products:wizard.blocked.help')}
                          />
                        </Col>
                      );
                    }
                    const fieldName =
                      `accounting.${rule.key.split('.')[1]}` as Path<ProductWizardFormData>;
                    return (
                      <Col span={12} key={rule.key}>
                        <WizardTextField name={fieldName} control={control} label={label} />
                      </Col>
                    );
                  })}
                </Row>
              ),
            },
          ]}
        />
      </FormSection>

      {/* SR0.2 — 補貨參數 replenishment planning (SHOULD-tier, collapsed,
          optional). Local state, merged into the request via alignmentFields();
          never create-required, never readiness-gating. */}
      <FormSection title={t('products:wizard.sections.planning')}>
        <Text style={{ ...styles.helpText, marginBottom: '8px' }}>
          {t('products:wizard.sections.planningHint')}
        </Text>
        <Collapse
          ghost
          items={[
            {
              key: 'planning',
              label: t('products:wizard.sections.planning'),
              children: (
                <Row gutter={[16, 16]}>
                  <Col span={8}>
                    <Text style={styles.fieldLabel}>
                      {t('products:fields.planning.reorderPoint')}
                    </Text>
                    <InputNumber
                      size="large"
                      min={0}
                      value={planning.reorderPoint ?? null}
                      onChange={(value) => setPlanningField('reorderPoint', value)}
                      style={styles.formInput}
                    />
                  </Col>
                  <Col span={8}>
                    <Text style={styles.fieldLabel}>
                      {t('products:fields.planning.preferredStockLevel')}
                    </Text>
                    <InputNumber
                      size="large"
                      min={0}
                      value={planning.preferredStockLevel ?? null}
                      onChange={(value) => setPlanningField('preferredStockLevel', value)}
                      style={styles.formInput}
                    />
                  </Col>
                  <Col span={8}>
                    <Text style={styles.fieldLabel}>
                      {t('products:fields.planning.reorderMultiple')}
                    </Text>
                    <InputNumber
                      size="large"
                      min={0}
                      value={planning.reorderMultiple ?? null}
                      onChange={(value) => setPlanningField('reorderMultiple', value)}
                      style={styles.formInput}
                    />
                  </Col>
                  <Col span={8}>
                    <Text style={styles.fieldLabel}>
                      {t('products:fields.planning.purchaseLeadTimeDays')}
                    </Text>
                    <InputNumber
                      size="large"
                      min={0}
                      precision={0}
                      value={planning.purchaseLeadTimeDays ?? null}
                      onChange={(value) => setPlanningField('purchaseLeadTimeDays', value)}
                      style={styles.formInput}
                    />
                  </Col>
                  <Col span={8}>
                    <Text style={styles.fieldLabel}>
                      {t('products:fields.planning.safetyStockDays')}
                    </Text>
                    <InputNumber
                      size="large"
                      min={0}
                      precision={0}
                      value={planning.safetyStockDays ?? null}
                      onChange={(value) => setPlanningField('safetyStockDays', value)}
                      style={styles.formInput}
                    />
                  </Col>
                  <Col span={8}>
                    <Text style={styles.fieldLabel}>
                      {t('products:fields.planning.expectedDemandChangePct')}
                    </Text>
                    <InputNumber
                      size="large"
                      value={planning.expectedDemandChangePct ?? null}
                      onChange={(value) => setPlanningField('expectedDemandChangePct', value)}
                      style={styles.formInput}
                    />
                  </Col>
                  <Col span={8}>
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <Switch
                        checked={planning.seasonalDemand === true}
                        onChange={(checked) => setPlanningField('seasonalDemand', checked)}
                      />
                      <Text>{t('products:fields.planning.seasonalDemand')}</Text>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <Switch
                        checked={planning.doNotOrder === true}
                        onChange={(checked) => setPlanningField('doNotOrder', checked)}
                      />
                      <Text>{t('products:fields.planning.doNotOrder')}</Text>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <Switch
                        checked={planning.preOrder === true}
                        onChange={(checked) => setPlanningField('preOrder', checked)}
                      />
                      <Text>{t('products:fields.planning.preOrder')}</Text>
                    </div>
                  </Col>
                </Row>
              ),
            },
          ]}
        />
      </FormSection>
    </>
  );

  // -- Step ⑤ 檢視 ----------------------------------------------------------
  const renderReviewStep = () => {
    const values = getValues();
    const subsidiaryLabel = SUBSIDIARY_OPTIONS.find(
      (option) => option.value === values.subsidiaryId
    );
    const summaryRow = (label: string, value: ReactNode) => (
      <Row style={{ marginBottom: 8 }} key={label}>
        <Col span={8}>
          <Text style={{ color: token.colorTextSecondary }}>{label}</Text>
        </Col>
        <Col span={16}>
          <Text>{value ?? '-'}</Text>
        </Col>
      </Row>
    );

    return (
      <>
        <ReadinessMeter
          title={t('products:wizard.reviewMeter.title')}
          ready={readinessPreview.ready}
          statusText={
            readinessPreview.ready ? t('products:readiness.ready') : t('products:readiness.blocked')
          }
          items={meterItems}
          className="tw-mb-8"
        />

        <FormSection title={t('products:wizard.review.identity')}>
          {summaryRow(t('products:fields.sku'), values.sku || '-')}
          {summaryRow(t('products:fields.displayName'), values.displayName || '-')}
          {summaryRow(
            t('products:fields.subsidiary'),
            subsidiaryLabel ? t(subsidiaryLabel.labelKey) : '-'
          )}
          {summaryRow(t('products:fields.category'), categoryBreadcrumb || '-')}
        </FormSection>

        <FormSection title={t('products:wizard.review.units')}>
          {summaryRow(
            t('products:sections.units'),
            `${values.stockUnit} / ${values.purchaseUnit} / ${values.saleUnit}`
          )}
          {summaryRow(
            t('products:fields.dimensions'),
            values.lengthMm && values.widthMm && values.heightMm
              ? `${values.lengthMm} × ${values.widthMm} × ${values.heightMm} mm`
              : '-'
          )}
          {summaryRow(t('products:fields.cbm'), cbm != null ? `${cbm.toFixed(4)} m³` : '-')}
          {summaryRow(
            t('products:fields.weight'),
            values.weightKg != null
              ? `${values.weightKg} ${t(`products:weightUnits.${weightUnit}`)}`
              : '-'
          )}
          {summaryRow(
            t('products:fields.fitmentMakes'),
            values.fitmentMakes.length > 0 ? values.fitmentMakes.join(', ') : '-'
          )}
          {summaryRow(
            t('products:fields.fitmentModels'),
            values.fitmentModels.length > 0 ? values.fitmentModels.join(', ') : '-'
          )}
        </FormSection>

        <FormSection title={t('products:wizard.review.descriptions')}>
          {summaryRow(
            t('products:fields.salesDescription'),
            values.salesDescription?.trim() ? t('products:wizard.review.filled') : '-'
          )}
          {summaryRow(
            t('products:fields.purchaseDescription'),
            values.purchaseDescription?.trim() ? t('products:wizard.review.filled') : '-'
          )}
          {summaryRow(
            t('products:fields.customsDescription'),
            values.customsDescription?.trim() || '-'
          )}
          {summaryRow(
            t('products:fields.customerPartNumbers'),
            String(values.customerPartNumbers.length)
          )}
          {summaryRow(
            t('products:fields.sparePartNumbers'),
            String(values.sparePartNumbers.length)
          )}
          {summaryRow(t('products:fields.vendorLines'), String(values.vendorLines.length))}
          {summaryRow(t('products:fields.images'), String(values.images.length))}
          {summaryRow(t('products:fields.productNotes'), String(productNotes.length))}
        </FormSection>

        {/* PROPOSED (RULING-3 2026-08-29) — dual keys, legacy at flag-off. */}
        <FormSection
          title={t(
            pricingOn ? 'products:wizard.review.pricingCost' : 'products:wizard.review.commercial'
          )}
        >
          {summaryRow(
            t('products:fields.costingMethod'),
            values.costingMethod ? t(`products:wizard.costingMethods.${values.costingMethod}`) : '-'
          )}
          {summaryRow(
            t('products:fields.basePrice'),
            values.basePrice != null ? `${values.basePrice} ${values.currency}` : '-'
          )}
        </FormSection>
      </>
    );
  };

  const stepContent = [
    renderIdentityStep,
    renderUnitsStep,
    renderDescriptionsStep,
    renderCommercialStep,
    renderReviewStep,
  ][currentStep];

  const handleViewConflict = () => {
    const conflictId = skuCheck.conflict?.id;
    if (!conflictId) return;
    setConflictModalOpen(false);
    if (isDirty) {
      confirmUnsavedChanges(() => navigate(`/auth/products/${conflictId}`));
    } else {
      navigate(`/auth/products/${conflictId}`);
    }
  };

  if (isEditMode && productLoading && !existingProduct) {
    return (
      <div style={{ padding: token.paddingXL, textAlign: 'center' }}>
        <LoadingOutlined style={{ fontSize: token.sizeLG, color: token.colorPrimary }} />
      </div>
    );
  }

  return (
    <>
      <WizardShell
        steps={steps}
        currentStep={currentStep}
        onStepChange={handleStepChange}
        stepErrors={stepErrors}
        texts={shellTexts}
        mode={isEditMode ? 'edit' : 'create'}
        isSaving={isSaving}
        hasUnsavedChanges={isDirty}
        onSave={handleSave}
        onSubmit={handleCreate}
      >
        {/* G-2169-C-021 (DD!21 停用 Inactive / `isActive`, 0708-J Must) — the
            create-wizard header slot: a status Switch on the wizard chrome,
            default ON/Active, NO asterisk (Must-visible ≠ required-to-save;
            registry `requiredAtCreate` stays false). Mirrors the detail
            ProductHeader status row (icon + Status label + value + scale-0.8
            Switch). Edit mode keeps the detail header as the single owner of
            the toggle, so this strip is create-only; the form value hydrates
            from the read, and an explicit Inactive choice reaches the wire
            through `productApi.setActive` after POST — never through
            `toUpdateProductRequest`, which is isActive-free under D-3. */}
        {!isEditMode && (
          <div
            className="tw-flex tw-items-center tw-justify-end tw-gap-2"
            style={{ marginBottom: '16px' }}
          >
            <CheckCircleOutlined
              style={{ fontSize: token.fontSize, color: token.colorTextSecondary }}
            />
            <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
              {t('common:status.title')}
            </span>
            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <>
                  <span style={{ fontSize: token.fontSize, fontWeight: token.fontWeightStrong }}>
                    {t(field.value ? 'common:status.active' : 'common:status.inactive')}
                  </span>
                  <Switch
                    checked={field.value}
                    onChange={(checked) => field.onChange(checked)}
                    aria-label={t('products:fields.isActive')}
                    style={{ transform: 'scale(0.8)' }}
                  />
                </>
              )}
            />
          </div>
        )}
        {stepContent()}
      </WizardShell>

      {/* SKU conflict — "view existing" (dup-check FSM surface) */}
      <Modal
        open={conflictModalOpen}
        title={t('products:wizard.conflictModal.title')}
        okText={t('products:wizard.conflictModal.view')}
        cancelText={t('common:actions.close')}
        onCancel={() => setConflictModalOpen(false)}
        onOk={handleViewConflict}
        okButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
        cancelButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
      >
        {skuCheck.conflict && (
          <div>
            <Row style={{ marginBottom: 8 }}>
              <Col span={10}>
                <Text style={{ color: token.colorTextSecondary }}>
                  {t('products:fields.internalProductId')}
                </Text>
              </Col>
              <Col span={14}>
                <Text strong>{skuCheck.conflict.internalProductId}</Text>
              </Col>
            </Row>
            <Row style={{ marginBottom: 8 }}>
              <Col span={10}>
                <Text style={{ color: token.colorTextSecondary }}>{t('products:fields.sku')}</Text>
              </Col>
              <Col span={14}>
                <Text>{skuCheck.conflict.sku}</Text>
              </Col>
            </Row>
            <Row style={{ marginBottom: 8 }}>
              <Col span={10}>
                <Text style={{ color: token.colorTextSecondary }}>
                  {t('products:fields.displayName')}
                </Text>
              </Col>
              <Col span={14}>
                <Text>{skuCheck.conflict.displayName}</Text>
              </Col>
            </Row>
            <Row>
              <Col span={10}>
                <Text style={{ color: token.colorTextSecondary }}>
                  {t('products:wizard.conflictModal.status')}
                </Text>
              </Col>
              <Col span={14}>
                <Tag color={skuCheck.conflict.isActive ? 'success' : 'default'}>
                  {skuCheck.conflict.isActive
                    ? t('common:status.active')
                    : t('common:status.inactive')}
                </Tag>
              </Col>
            </Row>
          </div>
        )}
      </Modal>

      {/* S7.4 OEM dup prompt — verbatim board copy; advisory, never a gate */}
      <Modal
        open={oemPromptOpen}
        title={t('products:oemDup.promptTitle')}
        width={640}
        onCancel={() => setOemPromptOpen(false)}
        footer={
          <div className="tw-flex tw-justify-end tw-gap-2" style={{ flexWrap: 'wrap' }}>
            <Button
              onClick={() => setOemPromptOpen(false)}
              style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
            >
              {t('products:oemDup.backToEdit')}
            </Button>
            <Button
              onClick={handleOemDecline}
              style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
            >
              {t('products:oemDup.treatAsDifferent')}
            </Button>
            {/* Hide-don't-disable: the link CTA only exists when a linkable
                (SCN-carrying) match is on offer. */}
            {oemLinkable.length > 0 && (
              <Button
                type="primary"
                onClick={handleOemLink}
                style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
              >
                {t('products:oemDup.linkAction')}
              </Button>
            )}
          </div>
        }
      >
        {oemHasSameMarket && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: '12px' }}
            message={t('products:oemDup.sameMarketPrompt')}
          />
        )}
        {oemHasCrossMarket && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: '12px' }}
            message={t('products:oemDup.crossMarketPrompt')}
          />
        )}
        <Text strong style={{ display: 'block', marginBottom: '4px' }}>
          {t('products:oemDup.confirmCorrect')}
        </Text>
        <Text style={{ display: 'block', marginBottom: '12px' }}>
          {t('products:oemDup.confirmSameEntity')}
        </Text>
        <Text type="secondary" style={{ display: 'block', marginBottom: '8px' }}>
          {t('products:oemDup.matchListTitle')}
        </Text>
        <TableContainer>
          <Table<OemDupMatch>
            dataSource={oemCheck.matches}
            rowKey="id"
            size="small"
            pagination={false}
            columns={[
              {
                title: t('products:oemDup.columns.internalProductId'),
                dataIndex: 'internalProductId',
                key: 'internalProductId',
              },
              {
                title: t('products:oemDup.columns.market'),
                dataIndex: 'subsidiaryId',
                key: 'market',
                render: (value: string) => subsidiaryLabel(value),
              },
              {
                title: t('products:oemDup.columns.oemNo'),
                dataIndex: 'oemNo',
                key: 'oemNo',
              },
              {
                title: t('products:oemDup.columns.scn'),
                dataIndex: 'scn',
                key: 'scn',
                render: (value: string | null) => (value ? <PurpleTag>{value}</PurpleTag> : '-'),
              },
            ]}
          />
        </TableContainer>
        {!isEditMode && oemLinkable.length > 0 && (
          <div style={{ marginTop: '16px' }}>
            <Text style={styles.fieldLabel}>{t('products:oemDup.selectShared')}</Text>
            <Select
              value={oemPromptScn}
              size="large"
              style={{ width: '100%' }}
              options={oemLinkable.map((match) => ({
                value: match.scn as string,
                label: `${match.scn} · ${match.internalProductId} · ${subsidiaryLabel(
                  match.subsidiaryId
                )}`,
              }))}
              onChange={(value: string) => setOemPromptScn(value)}
            />
            <Text style={styles.helpText}>{t('products:oemDup.autoCarryNote')}</Text>
          </div>
        )}
        {oemCheck.matches.length > 0 && oemLinkable.length === 0 && (
          <Alert
            type="info"
            showIcon
            style={{ marginTop: '12px' }}
            message={t('products:oemDup.noLinkableNote')}
          />
        )}
      </Modal>

      {/* S7.4 edit-mode 選擇式關聯 — the S7.3 drawer applies the chosen SCN */}
      {isEditMode && existingProduct && (
        <ScnLinkDrawer
          open={scnDrawerOpen}
          product={existingProduct}
          onClose={() => setScnDrawerOpen(false)}
          onLinked={() => {
            void refreshExistingProduct();
          }}
        />
      )}

      {/* Inline leaf create for the cascader */}
      <CreateNodeModal
        open={createNodeOpen}
        parentId={createNodeParentId}
        nodes={taxonomyNodes}
        onClose={() => setCreateNodeOpen(false)}
        onCreated={(newNodeId) => {
          setValue('categoryId', newNodeId, { shouldDirty: true });
          trigger('categoryId');
        }}
      />

      {/* Row modals */}
      <FitmentYearModal
        open={yearModal.open}
        initial={yearModal.row}
        onClose={() => setYearModal({ open: false, row: null })}
        onSave={upsertYearRow}
      />
      <CustomerPartNumberModal
        open={pnModal.open}
        initial={pnModal.row}
        customerOptions={customerOptions}
        customersLoading={customersLoading}
        onClose={() => setPnModal({ open: false, row: null })}
        onSave={upsertPnRow}
      />
      <SparePartNumberModal
        open={sparePnModal.open}
        initial={sparePnModal.row}
        onClose={() => setSparePnModal({ open: false, row: null })}
        onSave={upsertSparePnRow}
      />
      <VendorLineModal
        open={vendorModal.open}
        initial={vendorModal.row}
        vendorOptions={vendorOptions}
        vendorsLoading={vendorsLoading}
        forcePrimary={vendorLines.length === 0}
        onClose={() => setVendorModal({ open: false, row: null })}
        onSave={upsertVendorLine}
      />
      <ImageSlotModal
        open={imageModal.open}
        initial={imageModal.row}
        nextSlot={Math.min(images.length + 1, 5)}
        onClose={() => setImageModal({ open: false, row: null })}
        onSave={upsertImageRow}
      />
    </>
  );
};

export default ProductFormWizard;
